import { useState, useEffect, useLayoutEffect, useRef, useCallback, lazy, Suspense } from "react";
import { api, getToken } from "./lib/api";
import { SignInPage, SignUpPage } from "./components/AuthGate";
import { hasSession, clearSession } from "./lib/session";
import { LandingPage } from "./components/LandingPage";
import { PageShell } from "./components/PageShell";
import { getRoute, getConsolePage, navigate as goRoute, CONSOLE_PAGES } from "./lib/routes";
import { readLogsDomain, persistLogsDomain } from "./lib/mockLogs";
import { ConsoleIcon } from "./components/ConsoleIcon";
import { readTheme, saveTheme } from "./lib/theme";
import { Dashboard } from "./components/Dashboard";
import { KnowledgeBases, Documents } from "./components/SourceLibrary";
import { Agents, Analytics } from "./components/Activity";
import { APIKeys } from "./components/APIKeys";
import { Users, Billing } from "./components/AccountPages";
import { AuditLogs, Notifications, Settings } from "./components/WorkspaceSettings";
import "./styles/production-console.css";
import "./styles/enterprise-theme.css";
import "./styles/sovereign-theme.css";

/** When false, Learn · Topics / Learn · K8s are hidden from nav; knowledge still answers in Chat. */
const SHOW_LEARN_UI = false;

const Investigations = lazy(() => import("./components/Investigations").then(m => ({ default: m.Investigations })));
const WorkspaceDashboards = lazy(() => import("./components/WorkspaceDashboards").then(m => ({ default: m.WorkspaceDashboards })));
const ReleaseNotes = lazy(() => import("./components/ReleaseNotes").then(m => ({ default: m.ReleaseNotes })));

// Heavy routes — code-split so the first paint stays light.
const KubernetesLearn = lazy(() =>
  import("./components/KubernetesLearn").then((m) => ({ default: m.KubernetesLearn }))
);
const TopicsLearn = lazy(() =>
  import("./components/TopicsLearn").then((m) => ({ default: m.TopicsLearn }))
);
const LogsExplorer = lazy(() =>
  import("./components/LogsExplorer").then((m) => ({ default: m.LogsExplorer }))
);
const ItsmIncidents = lazy(() =>
  import("./components/ItsmIncidents").then((m) => ({ default: m.ItsmIncidents }))
);
const ItsmChanges = lazy(() =>
  import("./components/ItsmChanges").then((m) => ({ default: m.ItsmChanges }))
);
const ItsmCmdb = lazy(() =>
  import("./components/ItsmCmdb").then((m) => ({ default: m.ItsmCmdb }))
);
const Integrations = lazy(() =>
  import("./components/Integrations").then((m) => ({ default: m.Integrations }))
);
const Chat = lazy(() =>
  import("./components/Chat").then((m) => ({ default: m.Chat }))
);

function RouteFallback() {
  return (
    <div style={{ padding: 48, color: "var(--text2)", fontFamily: "var(--serif)" }}>
      Loading…
    </div>
  );
}

const FONT_LINK = document.createElement("link");
FONT_LINK.rel = "stylesheet";
FONT_LINK.href = "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400&family=IBM+Plex+Sans:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&family=Outfit:wght@500;600;700&display=swap";
document.head.appendChild(FONT_LINK);

const STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    /* ModelPilot Sign Up stake → Wecrew Copilot */
    --bg:       #f4f1ea;
    --bg-2:     #ede8df;
    --surface:  #faf7f0;
    --card:     #fffdf6;
    --paper-warm: linear-gradient(165deg, #faf7f0 0%, #f4f1ea 48%, #ede8df 100%);
    --border:   rgba(14,17,22,0.09);
    --border2:  rgba(14,17,22,0.18);
    --primary:  #ff5b2e;
    --primary2: #e04a20;
    --primaryL: #fff1ea;
    --accent:   #0f7a55;
    --accentL:  #e8f5ef;
    --accent-2: #2b4cff;
    --gold:     #8a6d2e;
    --text:     #0e1116;
    --text2:    #6d737d;
    --muted:    #a9adb3;
    --success:  #0f7a55;
    --successL: #e8f5ef;
    --warning:  #8a6d2e;
    --warningL: #f5efe0;
    --danger:   #dc2626;
    --dangerL:  #FEE2E2;
    --purple:   #2b4cff;
    --purpleL:  #eef0ff;
    --plum:     #7c3aed;
    --plumL:    #f3e8ff;
    --amber:    #ff5b2e;
    --amberL:   #fff1ea;
    --ink:      #0e1116;
    --paper:    #faf7f0;
    --sidebar-w: 224px;
    --sidebar-bg: #0e1116;
    --sidebar-surface: #141820;
    --sidebar-border: rgba(255,255,255,0.08);
    --sidebar-text: rgba(250,247,240,0.92);
    --sidebar-muted: rgba(250,247,240,0.42);
    --sidebar-hover: rgba(255,255,255,0.06);
    --sidebar-active-bg: rgba(255,91,46,0.14);
    --sidebar-active-text: #ffb89a;
    --top-h:    56px;
    --radius:   11px;
    --radius2:  14px;
    --shadow:   0 1px 4px rgba(14,17,22,0.06), 0 4px 16px rgba(14,17,22,0.04);
    --shadow2:  0 4px 20px rgba(14,17,22,0.10);
    --display:  'Instrument Serif', Georgia, serif;
    --serif:    'Fraunces', Georgia, serif;
    --mono-f:   'JetBrains Mono', ui-monospace, monospace;
  }
  html, body, #root {
    height: 100%;
    background: var(--paper-warm);
    background-color: var(--bg);
    color: var(--text);
    font-family: var(--serif);
    font-size: 14px;
    -webkit-font-smoothing: antialiased;
  }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 4px; }
  button { cursor: pointer; border: none; background: none; color: inherit; font-family: inherit; }
  input, textarea, select { font-family: inherit; color: inherit; background: none; border: none; outline: none; }
  a { color: inherit; text-decoration: none; }
  .outfit { font-family: var(--display); font-weight: 400; letter-spacing: -0.01em; }
  .mono   { font-family: var(--mono-f); }

  /* ── Layout ── */
  .shell { display: flex; height: 100vh; overflow: hidden; }

  /* ── Sidebar ── */
  .sidebar {
    width: var(--sidebar-w); flex-shrink: 0;
    background: linear-gradient(180deg, var(--sidebar-bg) 0%, #12161e 100%);
    border-right: 1px solid var(--sidebar-border);
    color: var(--sidebar-text);
    display: flex; flex-direction: column;
    overflow: hidden; z-index: 200;
    transition: transform 0.28s cubic-bezier(.4,0,.2,1), box-shadow 0.28s;
  }
  @media (max-width: 768px) {
    .sidebar {
      position: fixed; top: 0; left: 0; bottom: 0;
      transform: translateX(-100%);
      box-shadow: none;
    }
    .sidebar.open {
      transform: translateX(0);
      box-shadow: 4px 0 32px rgba(0,0,0,0.15);
    }
    .overlay {
      position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 190;
      animation: fadeIn 0.2s ease;
    }
    @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  }
  .sidebar-logo {
    padding: 14px 18px;
    display: flex; align-items: center; gap: 10px;
    border-bottom: 1px solid var(--sidebar-border);
    flex-shrink: 0;
  }
  .logo-mark {
    width: 34px; height: 34px; border-radius: 10px;
    background: conic-gradient(from 210deg, var(--primary), var(--accent-2), var(--accent), var(--gold), var(--primary));
    display: grid; place-items: center; position: relative;
    font-size: 17px; font-style: italic; color: var(--paper);
    font-family: var(--display); flex-shrink: 0;
    box-shadow: 0 6px 18px rgba(255,91,46,.28);
  }
  .logo-mark::after { content: ""; position: absolute; inset: 2px; border-radius: 8px; background: var(--ink); }
  .logo-mark span { position: relative; z-index: 2; }
  .logo-text { font-family: var(--display); font-weight: 400; font-size: 17px; letter-spacing: 0.2px; color: var(--sidebar-text); }
  .logo-text em { font-style: italic; color: #ff8f6b; }
  .logo-badge { font-size: 9px; font-weight: 600; font-family: var(--mono-f); letter-spacing: 0.5px; text-transform: uppercase; background: rgba(255,91,46,0.18); color: #ffb89a; padding: 2px 6px; border-radius: 5px; margin-left: 4px; border: 1px solid rgba(255,91,46,0.35); }
  .sidebar-close { margin-left: auto; display: none; font-size: 18px; color: var(--sidebar-muted); padding: 4px; }
  @media (max-width: 768px) { .sidebar-close { display: block; } }
  .sidebar-nav { flex: 1; overflow-y: auto; padding: 8px 0; }
  .nav-section { padding: 10px 16px 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: var(--sidebar-muted); }
  .nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 12px; margin: 1px 8px; border-radius: 8px;
    cursor: pointer; font-size: 13.5px; font-weight: 500; color: var(--sidebar-muted);
    transition: all 0.15s; border: 1px solid transparent;
  }
  .nav-item:hover { background: var(--sidebar-hover); color: var(--sidebar-text); }
  .nav-item.active { background: var(--sidebar-active-bg); color: var(--sidebar-active-text); font-weight: 600; border-color: rgba(255,91,46,0.28); }
  .nav-icon { font-size: 16px; width: 20px; text-align: center; flex-shrink: 0; }
  .nav-badge { margin-left: auto; background: var(--primary); color: #fff; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 10px; }
  .nav-badge.red { background: var(--danger); }
  .sidebar-footer {
    padding: 12px;
    border-top: 1px solid var(--sidebar-border);
    display: flex; align-items: center; gap: 10px;
    cursor: pointer; flex-shrink: 0;
    background: var(--sidebar-surface);
  }
  .sidebar-footer:hover { background: rgba(255,255,255,0.04); }
  .sidebar-signout-btn {
    margin-left: auto; padding: 6px 10px; border-radius: 8px;
    font-size: 11px; font-weight: 700; color: var(--danger);
    border: 1.5px solid #FECACA; background: var(--dangerL); flex-shrink: 0;
  }
  .sidebar-footer:hover .sidebar-signout-btn { background: #FEE2E2; }
  .avatar { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), var(--purple)); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .user-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--sidebar-text); }
  .user-role { font-size: 11px; color: var(--sidebar-muted); }

  /* ── Topbar ── */
  .topbar {
    height: var(--top-h); flex-shrink: 0;
    background: rgba(255, 253, 246, 0.94);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(14,17,22,0.08);
    display: flex; align-items: center; gap: 10px;
    padding: 0 16px;
    box-shadow: 0 1px 0 rgba(255,255,255,0.85) inset, 0 4px 22px rgba(14,17,22,0.06);
  }
  .hamburger { display: none; width: 36px; height: 36px; border-radius: 8px; align-items: center; justify-content: center; font-size: 20px; color: var(--text2); flex-shrink: 0; }
  @media (max-width: 768px) { .hamburger { display: flex; } }
  .topbar-title { font-family: var(--display); font-weight: 700; font-size: 16px; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--text); }
  .topbar-search {
    display: flex; align-items: center; gap: 8px;
    background: var(--bg); border: 1.5px solid var(--border);
    border-radius: 9px; padding: 0 12px; height: 36px; width: 220px;
    transition: border-color 0.15s;
  }
  .topbar-search:focus-within { border-color: var(--primary); }
  .topbar-search input { flex: 1; font-size: 13px; color: var(--text); background: transparent; }
  .topbar-search input::placeholder { color: var(--muted); }
  @media (max-width: 600px) { .topbar-search { display: none; } }
  .topbar-btn { width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center; background: var(--bg); border: 1.5px solid var(--border); font-size: 16px; color: var(--text2); transition: all 0.15s; position: relative; flex-shrink: 0; }
  .topbar-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--primaryL); }
  .notif-dot { position: absolute; top: 5px; right: 5px; width: 7px; height: 7px; background: var(--danger); border-radius: 50%; border: 2px solid var(--surface); }
  .user-menu-wrap { position: relative; flex-shrink: 0; }
  .user-menu {
    position: absolute; top: calc(100% + 8px); right: 0; min-width: 188px;
    background: var(--surface); border: 1.5px solid var(--border); border-radius: 12px;
    box-shadow: 0 16px 40px rgba(14,17,22,.12); padding: 6px; z-index: 300;
    animation: fadeIn 0.15s ease;
  }
  .user-menu-head { padding: 10px 12px 8px; border-bottom: 1px solid var(--border); margin-bottom: 4px; }
  .user-menu-name { font-size: 13px; font-weight: 700; color: var(--text); }
  .user-menu-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
  .user-menu-item {
    width: 100%; display: flex; align-items: center; gap: 8px;
    padding: 9px 12px; border-radius: 8px; font-size: 13px; font-weight: 600;
    color: var(--text2); text-align: left; transition: all 0.15s;
  }
  .user-menu-item:hover { background: var(--bg); color: var(--text); }
  .user-menu-item.danger { color: var(--danger); }
  .user-menu-item.danger:hover { background: var(--dangerL); }

  /* ── Main ── */
  .main {
    flex: 1; display: flex; flex-direction: column; min-width: 0; overflow: hidden;
    position: relative;
    background:
      radial-gradient(ellipse 68% 42% at 92% 6%, rgba(43,76,255,0.06), transparent 55%),
      radial-gradient(ellipse 58% 38% at 4% 94%, rgba(15,122,85,0.05), transparent 58%),
      radial-gradient(ellipse 48% 32% at 50% -4%, rgba(255,91,46,0.045), transparent 52%),
      linear-gradient(180deg, #faf7f0 0%, #f4f1ea 42%, #f0ece4 100%);
  }
  .main::before {
    content: ""; position: absolute; inset: 0; pointer-events: none; z-index: 0;
    opacity: 0.16; mix-blend-mode: multiply;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6 0 0 0 0 0.55 0 0 0 0 0.45 0 0 0 0.09 0'/></filter><rect width='240' height='240' filter='url(%23n)'/></svg>");
  }
  .main > * { position: relative; z-index: 1; }
  .content { flex: 1; overflow-y: auto; padding: 20px; background: transparent; }
  .content:has(.chat-shell) { padding: 0; overflow: hidden; }
  @media (max-width: 600px) { .content { padding: 14px; } .content:has(.chat-shell) { padding: 0; } }

  /* ── Cards ── */
  .card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 20px; box-shadow: var(--shadow); }
  @media (max-width: 600px) { .card { padding: 14px; } }
  .card-title { font-family: var(--display); font-weight: 700; font-size: 15px; margin-bottom: 4px; color: var(--text); }
  .card-sub { font-size: 12px; color: var(--text2); }

  /* ── KPI grid ── */
  .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 18px; }
  @media (max-width: 900px) { .kpi-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .kpi-grid { grid-template-columns: 1fr 1fr; gap: 10px; } }
  .kpi { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px 16px; position: relative; box-shadow: var(--shadow); overflow: hidden; }
  .kpi::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; border-radius: 12px 12px 0 0; }
  .kpi.blue::before { background: var(--primary); }
  .kpi.teal::before { background: var(--accent); }
  .kpi.green::before { background: var(--success); }
  .kpi.amber::before { background: var(--warning); }
  .kpi-icon { width: 36px; height: 36px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 17px; margin-bottom: 10px; }
  .kpi.blue .kpi-icon  { background: var(--primaryL); }
  .kpi.teal .kpi-icon  { background: var(--accentL); }
  .kpi.green .kpi-icon { background: var(--successL); }
  .kpi.amber .kpi-icon { background: var(--warningL); }
  .kpi-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text2); margin-bottom: 6px; }
  .kpi-value { font-family: var(--display); font-weight: 800; font-size: 26px; letter-spacing: -0.5px; color: var(--text); }
  .kpi-change { font-size: 11px; margin-top: 4px; font-weight: 600; }
  .kpi-change.up { color: var(--success); }
  .kpi-change.dn { color: var(--danger); }

  /* ── Grid helpers ── */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
  @media (max-width: 900px) { .grid-3 { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .grid-2, .grid-3 { grid-template-columns: 1fr; gap: 10px; } }
  .flex { display: flex; }
  .flex-col { display: flex; flex-direction: column; }
  .items-center { align-items: center; }
  .justify-between { justify-content: space-between; }
  .gap-8  { gap: 8px; }
  .gap-12 { gap: 12px; }
  .gap-16 { gap: 16px; }
  .mb-4  { margin-bottom: 4px; }
  .mb-8  { margin-bottom: 8px; }
  .mb-14 { margin-bottom: 14px; }
  .mb-20 { margin-bottom: 20px; }
  .mt-8  { margin-top: 8px; }
  .mt-12 { margin-top: 12px; }
  .flex-1 { flex: 1; }
  .min-w-0 { min-width: 0; }
  .w-full  { width: 100%; }
  .text-sm { font-size: 12px; }
  .text-xs { font-size: 11px; }
  .text-muted   { color: var(--text2); }
  .text-primary { color: var(--primary); }
  .text-success { color: var(--success); }
  .text-danger  { color: var(--danger); }
  .text-warning { color: var(--warning); }
  .fw-500 { font-weight: 500; }
  .fw-600 { font-weight: 600; }
  .fw-700 { font-weight: 700; }

  /* ── Buttons ── */
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 9px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; border: 1.5px solid transparent; white-space: nowrap; }
  .btn-primary { background: var(--primary); color: #fff; box-shadow: 0 2px 8px rgba(255,91,46,0.22); }
  .btn-primary:hover { background: var(--primary2); }
  .btn-outline { background: transparent; border-color: var(--border2); color: var(--text2); }
  .btn-outline:hover { border-color: var(--primary); color: var(--primary); background: var(--primaryL); }
  .btn-ghost { background: transparent; color: var(--text2); }
  .btn-ghost:hover { background: var(--bg); color: var(--text); }
  .btn-danger { background: var(--dangerL); color: var(--danger); border-color: #FECACA; }
  .btn-danger:hover { background: #FEE2E2; }
  .btn-sm { padding: 5px 10px; font-size: 12px; border-radius: 7px; }
  .btn-icon { width: 32px; height: 32px; padding: 0; border-radius: 8px; justify-content: center; }

  /* ── Tags ── */
  .tag { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; }
  .tag-blue   { background: var(--primaryL);  color: var(--primary); }
  .tag-teal   { background: var(--accentL);   color: var(--accent); }
  .tag-green  { background: var(--successL);  color: var(--success); }
  .tag-amber  { background: var(--warningL);  color: var(--warning); }
  .tag-red    { background: var(--dangerL);   color: var(--danger); }
  .tag-purple { background: var(--purpleL);   color: var(--purple); }

  /* ── Table ── */
  .table { width: 100%; border-collapse: collapse; }
  .table th { text-align: left; padding: 10px 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); border-bottom: 1.5px solid var(--border); background: var(--bg); }
  .table td { padding: 12px 14px; border-bottom: 1px solid var(--border); font-size: 13px; }
  .table tr:last-child td { border-bottom: none; }
  .table tr:hover td { background: rgba(250,247,240,0.85); }
  .table-wrap { overflow-x: auto; }

  /* ── Progress ── */
  .prog-bar { height: 5px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .prog-fill { height: 100%; border-radius: 99px; transition: width 0.8s ease; }

  /* ── Inputs ── */
  .input-wrap { display: flex; align-items: center; gap: 8px; background: var(--bg); border: 1.5px solid var(--border); border-radius: 9px; padding: 0 12px; height: 38px; transition: border-color 0.15s; }
  .input-wrap:focus-within { border-color: var(--primary); }
  .input-wrap input { flex: 1; font-size: 13px; background: transparent; }
  .input-wrap input::placeholder { color: var(--muted); }
  .input-full { width: 100%; background: var(--bg); border: 1.5px solid var(--border); border-radius: 9px; padding: 10px 12px; font-size: 13.5px; color: var(--text); transition: border-color 0.15s; }
  .input-full:focus { border-color: var(--primary); outline: none; background: #fff; }

  /* ── Dot ── */
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .dot-green { background: var(--success); }
  .dot-amber { background: var(--warning); }
  .dot-red   { background: var(--danger); }
  .dot-blue  { background: var(--primary); }

  /* ── Divider ── */
  .divider { height: 1px; background: var(--border); margin: 16px 0; }

  /* ── Toggle ── */
  .toggle { width: 40px; height: 22px; border-radius: 11px; background: var(--border2); position: relative; cursor: pointer; transition: background 0.2s; flex-shrink: 0; border: 1.5px solid transparent; }
  .toggle.on { background: var(--primary); }
  .toggle::after { content: ''; position: absolute; width: 16px; height: 16px; border-radius: 50%; background: #fff; top: 1px; left: 1px; transition: transform 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
  .toggle.on::after { transform: translateX(18px); }

  /* ── Sparkline ── */
  .sparkline { height: 50px; display: flex; align-items: flex-end; gap: 3px; }
  .spark-bar { flex: 1; background: linear-gradient(180deg, var(--primary), var(--accent-2)); border-radius: 3px 3px 0 0; opacity: 0.75; min-height: 4px; transition: opacity 0.15s; }
  .spark-bar:hover { opacity: 1; }

  /* ── Chat ── */
  .chat-shell { display: flex; flex-direction: column; height: calc(100vh - var(--top-h)); background: transparent; }
  .chat-toolbar {
    padding: 10px 16px; border-bottom: 1px solid rgba(14,17,22,0.08);
    background: rgba(250,247,240,0.92);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0;
  }
  .chat-toolbar-label { text-transform: uppercase; letter-spacing: 0.5px; }
  .chat-kb-select {
    background: var(--bg); border: 1.5px solid var(--border); border-radius: 8px;
    padding: 5px 10px; font-size: 13px; color: var(--text); min-width: 180px;
  }
  .chat-clear-btn { margin-left: auto; }
  .chat-msgs {
    flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 14px;
    background:
      radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,253,246,0.55), transparent 70%),
      linear-gradient(180deg, rgba(250,247,240,0.35) 0%, rgba(244,241,234,0.55) 100%);
  }
  .msg { display: flex; gap: 10px; max-width: 800px; }
  .msg.user { flex-direction: row-reverse; margin-left: auto; }
  .msg-avatar { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; }
  .msg-avatar.ai { background: linear-gradient(135deg, var(--primary), var(--accent-2)); color: #fff; }
  .msg-avatar.user { background: linear-gradient(135deg, var(--purple), var(--primary)); color: #fff; }
  .msg-bubble { background: rgba(255,253,246,0.96); border: 1.5px solid rgba(14,17,22,0.09); border-radius: 12px; padding: 12px 15px; font-size: 14px; line-height: 1.65; color: var(--text); box-shadow: var(--shadow); }
  .msg.user .msg-bubble { background: linear-gradient(135deg, #fff1ea 0%, #fff8f4 100%); border-color: rgba(255,91,46,0.28); }
  .msg-meta { font-size: 11px; color: var(--muted); margin-top: 4px; }
  .chat-input-area {
    padding: 12px 16px; border-top: 1px solid rgba(14,17,22,0.08);
    background: rgba(255,253,246,0.96);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 -4px 24px rgba(14,17,22,0.04);
    flex-shrink: 0;
  }
  .chat-input-box {
    display: flex; align-items: flex-end; gap: 8px;
    background: #faf7f0; border: 1.5px solid rgba(14,17,22,0.12);
    border-radius: 12px; padding: 10px 12px; transition: border-color 0.15s, box-shadow 0.15s;
    box-shadow: inset 0 1px 2px rgba(14,17,22,0.04);
  }
  .chat-input-box:focus-within { border-color: var(--primary); background: #fffdf6; box-shadow: 0 0 0 3px rgba(255,91,46,0.1), inset 0 1px 2px rgba(14,17,22,0.03); }
  .chat-textarea { flex: 1; resize: none; font-size: 14px; line-height: 1.5; min-height: 24px; max-height: 140px; color: var(--text); background: transparent; }
  .chat-textarea::placeholder { color: var(--muted); }
  .send-btn { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: var(--primary); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px; transition: all 0.15s; box-shadow: 0 2px 8px rgba(255,91,46,0.22); }
  .send-btn:hover { background: var(--primary2); }
  .send-btn:disabled { background: var(--border2); color: var(--muted); cursor: not-allowed; box-shadow: none; }
  .typing-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--primary); animation: tdot 1.2s infinite; margin: 0 2px; }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes tdot { 0%,80%,100%{transform:scale(0.6);opacity:0.3} 40%{transform:scale(1);opacity:1} }

  /* ── KB cards ── */
  .kb-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px; cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; gap: 12px; box-shadow: var(--shadow); }
  .kb-card:hover { border-color: var(--primary); transform: translateY(-2px); box-shadow: var(--shadow2); }
  .kb-icon { width: 44px; height: 44px; border-radius: 11px; display: flex; align-items: center; justify-content: center; font-size: 22px; }

  /* ── Agent cards ── */
  .agent-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px; transition: all 0.2s; box-shadow: var(--shadow); }
  .agent-card:hover { box-shadow: var(--shadow2); }

  /* ── Integration catalog ── */
  .int-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 16px; display: flex; align-items: center; gap: 14px; box-shadow: var(--shadow); transition: all 0.15s; }
  .int-card:hover { border-color: var(--border2); }
  .int-card-rich { cursor: pointer; align-items: flex-start; }
  .int-card-rich:hover { border-color: var(--primary); box-shadow: var(--shadow2); transform: translateY(-1px); }
  .int-card-rich.selected { border-color: var(--primary); background: var(--card); box-shadow: 0 0 0 1px var(--primary); }
  .int-logo { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 22px; background: var(--bg); border: 1.5px solid var(--border); flex-shrink: 0; }
  .int-card-actions { flex-shrink: 0; display: flex; flex-direction: column; gap: 6px; }
  .int-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 12px; }
  .int-search { flex: 1; min-width: 200px; max-width: 420px; }
  .int-status-filters, .int-cat-row { display: flex; flex-wrap: wrap; gap: 8px; }
  .int-cat-row { margin-bottom: 18px; }
  .int-filter-chip, .int-cat-chip {
    padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
    border: 1.5px solid var(--border); background: var(--surface); color: var(--text2);
    transition: all 0.15s;
  }
  .int-filter-chip:hover, .int-cat-chip:hover { border-color: var(--primary); color: var(--primary); background: var(--primaryL); }
  .int-filter-chip.active, .int-cat-chip.active { border-color: var(--primary); background: var(--primaryL); color: var(--primary); }
  .int-group { margin-bottom: 22px; }
  .int-group-title {
    font-family: var(--display); font-weight: 700; font-size: 13px;
    text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted);
    margin-bottom: 10px;
  }
  .int-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  @media (max-width: 900px) { .int-grid { grid-template-columns: 1fr; } }
  .int-env-chip {
    display: inline-block; font-size: 11px; padding: 4px 8px; border-radius: 6px;
    background: var(--bg); border: 1px solid var(--border); color: var(--text2);
  }
  .int-steps-list { margin: 0 0 8px 18px; padding: 0; }
  .int-steps-list li { font-size: 13px; color: var(--text2); line-height: 1.55; margin-bottom: 8px; }
  .int-drawer-backdrop {
    position: fixed; inset: 0; background: rgba(17, 24, 39, 0.35); z-index: 400;
    animation: fadeIn 0.2s ease;
  }
  .int-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: min(440px, 100vw);
    background: var(--surface); z-index: 410; display: flex; flex-direction: column;
    box-shadow: -8px 0 32px rgba(0,0,0,0.12);
    animation: intDrawerIn 0.28s cubic-bezier(.4,0,.2,1);
  }
  @keyframes intDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
  .int-drawer-head {
    padding: 18px 20px; border-bottom: 1px solid var(--border);
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-shrink: 0;
  }
  .int-drawer-body { flex: 1; overflow-y: auto; padding: 18px 20px; }
  .int-detail-block { margin-bottom: 20px; padding-bottom: 18px; border-bottom: 1px solid var(--border); }
  .int-detail-block:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
  .int-drawer-foot {
    padding: 14px 20px; border-top: 1px solid var(--border);
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0;
    background: var(--card);
  }
  .int-modal-backdrop {
    position: fixed; inset: 0; background: rgba(17, 24, 39, 0.45); z-index: 500;
    display: flex; align-items: center; justify-content: center; padding: 16px;
    animation: fadeIn 0.2s ease;
  }
  .int-modal {
    width: min(480px, 100%); background: var(--surface); border-radius: var(--radius2);
    border: 1.5px solid var(--border); box-shadow: var(--shadow2); overflow: hidden;
  }
  .int-modal-head {
    padding: 16px 18px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
  }
  .int-modal-body { padding: 18px; }
  .int-modal-foot {
    padding: 14px 18px; border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; gap: 10px; background: var(--card);
  }
  .int-setup-progress { display: flex; gap: 6px; margin-bottom: 14px; }
  .int-setup-dot { flex: 1; height: 4px; border-radius: 99px; background: var(--border); }
  .int-setup-dot.active { background: var(--primary); }
  .int-setup-step {
    display: flex; gap: 12px; align-items: flex-start;
    background: var(--bg); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px;
  }
  .int-setup-num {
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    background: var(--primaryL); color: var(--primary); font-weight: 800;
    display: flex; align-items: center; justify-content: center; font-size: 13px;
    font-family: var(--display);
  }
  .int-config-meta { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; }
  .int-config-meta .settings-label { display: block; margin-bottom: 6px; }
  .int-config-table-wrap { overflow-x: auto; border: 1px solid var(--border); border-radius: var(--radius); margin-bottom: 12px; }
  .int-config-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .int-config-table th {
    text-align: left; padding: 8px 10px; background: var(--bg-2); color: var(--text2);
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 1px solid var(--border);
  }
  .int-config-table td { padding: 8px 10px; border-bottom: 1px solid var(--border); vertical-align: top; }
  .int-config-table tr:last-child td { border-bottom: none; }
  .int-sample-env {
    margin: 0; padding: 12px 14px; border-radius: var(--radius); background: var(--ink); color: rgba(250,247,240,0.92);
    font-size: 11px; line-height: 1.55; overflow-x: auto; white-space: pre;
  }
  .int-env-box {
    background: linear-gradient(180deg, var(--card) 0%, var(--bg) 100%);
    border: 1.5px solid rgba(255,91,46,0.28); border-radius: 11px; padding: 12px 14px;
  }
  .flex-wrap { flex-wrap: wrap; }

  /* ── Notif item ── */
  .notif-item { display: flex; gap: 12px; padding: 13px 0; border-bottom: 1px solid var(--border); }
  .notif-item:last-child { border-bottom: none; }

  /* ── Audit row ── */
  .log-row { display: flex; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); align-items: flex-start; }
  .log-row:last-child { border-bottom: none; }

  /* ── Settings ── */
  .settings-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: var(--muted); margin-bottom: 10px; }
  .settings-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 0; border-bottom: 1px solid var(--border); }
  .settings-row:last-child { border-bottom: none; }

  /* ── Plan cards ── */
  .plan-card { background: var(--card); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 22px; box-shadow: var(--shadow); }
  .plan-card.active-plan { border-color: var(--primary); background: var(--card); }
  .plan-price { font-family: var(--display); font-weight: 800; font-size: 30px; letter-spacing: -0.5px; color: var(--text); }
  .plan-feature { display: flex; align-items: center; gap: 8px; font-size: 13px; padding: 5px 0; color: var(--text2); }

  /* ── Toast ── */
  .toast-wrap { position: fixed; bottom: 20px; right: 20px; z-index: 9999; display: flex; flex-direction: column; gap: 8px; }
  @media (max-width: 480px) { .toast-wrap { bottom: 12px; right: 12px; left: 12px; } }
  .toast { background: var(--surface); border: 1.5px solid var(--border); border-radius: 10px; padding: 11px 16px; font-size: 13px; display: flex; align-items: center; gap: 10px; box-shadow: 0 8px 28px rgba(0,0,0,0.12); animation: toastIn 0.3s ease; }
  @keyframes toastIn { from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }

  /* ── Ops structured chat ── */
  .chat-assistant-content { display: flex; flex-direction: column; gap: 12px; }
  .msg-text { line-height: 1.65; }
  .ops-structured { display: flex; flex-direction: column; gap: 10px; }
  .ops-panel, .ops-knowledge {
    background: var(--bg);
    border: 1.5px solid var(--border);
    border-radius: 11px;
    padding: 14px 16px;
  }
  .ops-panel-animate { animation: panelIn 0.35s ease both; }
  @keyframes panelIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  .ops-panel-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
  .ops-panel-icon { font-size: 18px; line-height: 1; margin-top: 2px; }
  .ops-panel-title { font-family: var(--display); font-weight: 700; font-size: 14px; color: var(--text); }
  .ops-panel-sub { font-size: 12px; color: var(--text2); margin-top: 2px; }
  .ops-kv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
  .ops-kv-grid-1 { grid-template-columns: 1fr; }
  @media (max-width: 560px) { .ops-kv-grid { grid-template-columns: 1fr; } }
  .ops-kv { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .ops-kv-row { flex-direction: row; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--border); }
  .ops-kv-row:last-child { border-bottom: none; padding-bottom: 0; }
  .ops-kv-key { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); }
  .ops-kv-val { font-size: 13px; font-weight: 500; color: var(--text); word-break: break-word; }
  .ops-logs-wrap { overflow-x: auto; margin: 0 -4px; }
  .ops-logs-table th, .ops-logs-table td { padding: 8px 10px; }
  .ops-knowledge { background: linear-gradient(180deg, var(--card) 0%, var(--bg) 100%); border-color: rgba(255,91,46,0.28); }
  .ops-knowledge-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
  .ops-knowledge-badge { display: inline-block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: var(--primary); background: var(--primaryL); padding: 3px 8px; border-radius: 6px; margin-bottom: 6px; }
  .ops-progress-ring {
    width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
    background: conic-gradient(var(--primary) calc(var(--pct) * 1%), var(--border) 0);
    display: flex; align-items: center; justify-content: center;
    position: relative;
  }
  .ops-progress-ring::before { content: ''; position: absolute; inset: 5px; background: var(--surface); border-radius: 50%; }
  .ops-progress-text { position: relative; font-size: 11px; font-weight: 700; color: var(--primary); font-family: var(--display); }
  .ops-steps { display: flex; flex-direction: column; gap: 10px; }
  .ops-step { display: flex; gap: 12px; padding: 12px; border-radius: 10px; border: 1.5px solid var(--border); background: var(--surface); transition: opacity 0.2s, border-color 0.2s; }
  .ops-step.done { opacity: 0.72; border-color: var(--success); background: var(--successL); }
  .ops-step-check { display: flex; align-items: flex-start; gap: 8px; cursor: pointer; flex-shrink: 0; padding-top: 2px; }
  .ops-step-check input { position: absolute; opacity: 0; width: 0; height: 0; }
  .ops-step-num {
    width: 26px; height: 26px; border-radius: 8px;
    background: var(--primaryL); color: var(--primary);
    font-family: var(--display); font-weight: 800; font-size: 12px;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s;
  }
  .ops-step.done .ops-step-num { background: var(--success); color: #fff; }
  .ops-step-body { flex: 1; min-width: 0; }
  .ops-step-title { font-weight: 700; font-size: 13px; margin-bottom: 4px; }
  .ops-step-instruction { font-size: 13px; color: var(--text2); line-height: 1.55; }
  .ops-callout { margin-top: 8px; padding: 8px 10px; border-radius: 8px; font-size: 12px; line-height: 1.5; }
  .ops-callout-info { background: var(--primaryL); color: var(--primary); border: 1px solid rgba(255,91,46,0.28); }
  .ops-callout-warn { background: var(--warningL); color: var(--warning); border: 1px solid #FDE68A; }
  .ops-command { margin-top: 8px; padding: 10px 12px; background: #0F172A; color: #E2E8F0; border-radius: 8px; font-size: 11.5px; line-height: 1.5; overflow-x: auto; white-space: pre-wrap; word-break: break-all; }
  .ops-resources { margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border); }
  .ops-resource-link { font-size: 12px; font-weight: 600; color: var(--primary); padding: 5px 10px; border-radius: 7px; background: var(--primaryL); transition: background 0.15s; }
  .ops-resource-link:hover { background: var(--primaryL); }
  .output-section { background: var(--bg); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px 16px; }
  .output-section-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
  .output-section-icon { font-size: 18px; line-height: 1; margin-top: 1px; }
  .output-section-title { font-family: var(--display); font-weight: 700; font-size: 14px; color: var(--text); }
  .output-section-sub { font-size: 12px; color: var(--text2); margin-top: 2px; }
  .sources-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .sources-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 9px; background: var(--surface); border: 1px solid var(--border); }
  .sources-num { width: 22px; height: 22px; border-radius: 6px; background: var(--primaryL); color: var(--primary); font-size: 11px; font-weight: 700; display: grid; place-items: center; flex-shrink: 0; font-family: var(--mono-f); }
  .sources-label { flex: 1; min-width: 0; font-size: 13px; font-weight: 500; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sources-score { font-size: 11px; color: var(--muted); flex-shrink: 0; }
  .ops-detail-card { background: var(--surface); border: 1px solid var(--border); border-radius: 9px; overflow: hidden; }
  .ops-detail-row { display: grid; grid-template-columns: minmax(120px, 38%) 1fr; gap: 12px; padding: 10px 14px; border-bottom: 1px solid var(--border); align-items: start; }
  .ops-detail-row:last-child { border-bottom: none; }
  .ops-detail-key { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); }
  .ops-detail-val { font-size: 13px; font-weight: 500; color: var(--text); word-break: break-word; }
  @media (max-width: 560px) { .ops-detail-row { grid-template-columns: 1fr; gap: 4px; } }
  .ops-sources { margin-top: 0; }
  /* ── Upgrade activity panels ── */
  .ops-upgrade-overview {
    background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%);
    border: 1.5px solid rgba(255,91,46,0.28);
    border-radius: 11px;
    padding: 14px 16px;
  }
  .ops-upgrade-overview-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
  .ops-upgrade-version { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
  .ops-upgrade-from, .ops-upgrade-to { font-size: 15px; font-weight: 700; color: var(--text); padding: 4px 10px; background: var(--surface); border-radius: 8px; border: 1px solid var(--border); }
  .ops-upgrade-to { color: var(--primary); border-color: rgba(255,91,46,0.28); background: var(--primaryL); }
  .ops-upgrade-arrow { font-size: 18px; font-weight: 800; color: var(--primary); }
  .ops-upgrade-kpi-strip { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px 14px; }
  .ops-upgrade-kpi { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .ops-upgrade-kpi-wide { grid-column: 1 / -1; }
  .ops-upgrade-kpi-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); }
  .ops-upgrade-kpi-val { font-size: 13px; font-weight: 600; color: var(--text); }
  .ops-upgrade-services { margin-top: 12px; padding-top: 12px; border-top: 1px dashed rgba(255,91,46,0.28); }
  .ops-config {
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: 11px;
    padding: 14px 16px;
  }
  .ops-config-step { background: var(--bg); }
  .itsm-change-detail { border-color: rgba(255,91,46,0.28); background: linear-gradient(180deg, var(--card) 0%, var(--surface) 100%); }
  .itsm-row-selected td { background: var(--primaryL) !important; }
  .chat-suggestions { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 16px 8px; }
  .chat-chip {
    font-size: 12px; font-weight: 600; color: var(--primary);
    background: var(--primaryL); border: 1.5px solid rgba(255,91,46,0.28);
    padding: 6px 12px; border-radius: 99px; cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .chat-chip:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(255,91,46,0.14); }
  .chat-chip-nav { background: linear-gradient(135deg, var(--primaryL) 0%, var(--card) 100%); border-color: rgba(255,91,46,0.28); font-weight: 700; }
  /* ── K8s teaching panels ── */
  .k8s-overview {
    background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%);
    border: 1.5px solid rgba(255,91,46,0.28);
    border-radius: 11px;
    padding: 14px 16px;
    margin-bottom: 12px;
  }
  .k8s-meta-row { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; align-items: center; }
  .k8s-teaching-strip { display: flex; flex-wrap: wrap; align-items: stretch; gap: 4px; margin-top: 14px; }
  .k8s-teaching-node { display: flex; align-items: center; gap: 4px; }
  .k8s-teaching-arrow { color: var(--primary); font-weight: 800; font-size: 14px; padding: 0 2px; }
  .k8s-teaching-card {
    background: var(--surface); border: 1px solid var(--border); border-radius: 10px;
    padding: 10px 12px; min-width: 88px; text-align: center;
  }
  .k8s-teaching-icon { font-size: 20px; display: block; margin-bottom: 4px; }
  .k8s-teaching-label { font-size: 11px; font-weight: 700; color: var(--text); }
  .k8s-teaching-desc { font-size: 10px; color: var(--muted); margin-top: 2px; line-height: 1.3; }
  .k8s-paths { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; margin: 12px 0; }
  .k8s-path-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 12px; }
  .k8s-comp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; margin-top: 12px; }
  .k8s-comp-card { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 12px; }
  .k8s-comp-head { display: flex; gap: 10px; align-items: flex-start; margin-bottom: 8px; }
  .k8s-comp-name { font-size: 13px; font-weight: 700; }
  .k8s-comp-port { font-size: 10px; color: var(--muted); }
  .k8s-comp-body { font-size: 12px; color: var(--text2); line-height: 1.55; margin-bottom: 8px; }
  .k8s-comp-tags { display: flex; flex-wrap: wrap; gap: 4px; }
  .k8s-concept { background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px 16px; margin-bottom: 12px; }
  .k8s-concept-block { margin-top: 12px; }
  .k8s-concept-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--primary); margin-bottom: 6px; }
  .k8s-related-chips { margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border); }
  .k8s-learn-hero { background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%); border: 1.5px solid rgba(255,91,46,0.28); border-radius: var(--radius2); padding: 20px; margin-bottom: 18px; overflow-x: auto; }
  .k8s-learn-flow { margin-top: 0; flex-wrap: nowrap; min-width: min-content; }
  .k8s-learn-tabs { display: flex; gap: 8px; margin-bottom: 16px; flex-wrap: wrap; }
  .k8s-learn-tab { font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 99px; border: 1.5px solid var(--border); background: var(--surface); cursor: pointer; transition: all 0.15s; }
  .k8s-learn-tab.active { background: var(--primary); color: #fff; border-color: var(--primary); }
  .k8s-learn-split { display: grid; grid-template-columns: 260px 1fr; gap: 16px; min-height: 420px; }
  @media (max-width: 768px) { .k8s-learn-split { grid-template-columns: 1fr; } }
  .k8s-learn-list { display: flex; flex-direction: column; gap: 4px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 8px; max-height: 520px; overflow-y: auto; }
  .k8s-learn-list-item { display: flex; gap: 10px; align-items: center; width: 100%; text-align: left; padding: 10px 12px; border: none; background: none; border-radius: 8px; cursor: pointer; transition: background 0.15s; }
  .k8s-learn-list-item:hover { background: var(--primaryL); }
  .k8s-learn-list-item.active { background: var(--primaryL); border-left: 3px solid var(--primary); }
  .k8s-learn-detail { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 20px; box-shadow: var(--shadow); }
  .k8s-learn-detail-head { display: flex; gap: 14px; align-items: center; margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid var(--border); }
  .k8s-learn-detail-icon { font-size: 32px; }
  .k8s-learn-install-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
  .k8s-learn-install-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 18px; box-shadow: var(--shadow); }
  .topics-learn-search-wrap { display: flex; align-items: center; gap: 10px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 10px 14px; margin-bottom: 16px; }
  .topics-learn-search { flex: 1; border: none; background: transparent; font-size: 14px; color: var(--text); outline: none; }
  .topics-learn-layout { display: grid; grid-template-columns: 240px 1fr; gap: 16px; min-height: 480px; }
  @media (max-width: 768px) { .topics-learn-layout { grid-template-columns: 1fr; } }
  .topics-learn-sidebar { display: flex; flex-direction: column; gap: 4px; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 10px; max-height: 640px; overflow-y: auto; }
  .topics-learn-sidebar-label { padding: 4px 8px 8px; text-transform: uppercase; letter-spacing: 0.4px; }
  .topics-learn-cat { display: flex; gap: 10px; align-items: center; width: 100%; text-align: left; padding: 10px 12px; border: none; background: none; border-radius: 8px; cursor: pointer; transition: background 0.15s; }
  .topics-learn-cat:hover { background: var(--primaryL); }
  .topics-learn-cat.active { background: var(--primaryL); border-left: 3px solid var(--primary); }
  .topics-learn-cat-icon { font-size: 18px; flex-shrink: 0; }
  .topics-learn-main { min-width: 0; }
  .topics-learn-cat-banner { display: flex; gap: 14px; align-items: center; background: linear-gradient(135deg, var(--card) 0%, var(--primaryL) 100%); border: 1.5px solid rgba(255,91,46,0.28); border-radius: var(--radius2); padding: 16px 18px; margin-bottom: 14px; }
  .topics-learn-cat-banner-icon { font-size: 28px; }
  .topics-learn-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 14px; }
  .topics-learn-card { background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); padding: 16px; box-shadow: var(--shadow); display: flex; flex-direction: column; gap: 8px; }
  .topics-learn-card-head { display: flex; flex-direction: column; gap: 8px; }
  .topics-learn-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .topics-learn-explanation { line-height: 1.6; margin: 0; flex: 1; }
  .topics-learn-prompt { opacity: 0.85; margin-top: 4px; }
  .topics-learn-card-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
  .topics-learn-empty { padding: 24px; text-align: center; }
  .chat-welcome-hero {
    text-align: center; padding: 32px 20px 18px; margin: 4px 16px 0;
    background: linear-gradient(180deg, rgba(255,253,246,0.98) 0%, rgba(250,247,240,0.88) 100%);
    border: 1px solid rgba(14,17,22,0.07);
    border-radius: var(--radius2);
    box-shadow: var(--shadow);
    animation: panelIn 0.4s ease both;
  }
  .chat-welcome-mark {
    width: 52px; height: 52px; border-radius: 14px; margin: 0 auto 12px;
    background: conic-gradient(from 210deg, var(--primary), var(--accent-2), var(--accent), var(--gold), var(--primary));
    display: grid; place-items: center; position: relative;
    font-family: var(--display); font-style: italic; font-size: 26px; color: var(--paper);
    box-shadow: 0 4px 16px rgba(255,91,46,0.28);
    animation: pulseMark 3s ease-in-out infinite;
  }
  .chat-welcome-mark::after { content: ""; position: absolute; inset: 3px; border-radius: 11px; background: var(--ink); }
  .chat-welcome-mark span { position: relative; z-index: 1; }
  @keyframes pulseMark { 0%,100%{box-shadow:0 4px 16px rgba(255,91,46,0.28)} 50%{box-shadow:0 6px 24px rgba(255,91,46,0.42)} }
  .chat-welcome-title { font-family: var(--display); font-weight: 400; font-size: 22px; margin-bottom: 6px; }
  .chat-welcome-title em { font-style: italic; color: var(--primary); }
  .chat-welcome-sub { font-size: 13px; color: var(--text2); max-width: 420px; margin: 0 auto; line-height: 1.55; }
  .ops-glance { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 18px; }
  @media (max-width: 768px) { .ops-glance { grid-template-columns: 1fr; } }
  .ops-glance-item {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; background: var(--surface);
    border: 1.5px solid var(--border); border-radius: var(--radius2);
    box-shadow: var(--shadow); transition: border-color 0.2s;
  }
  .ops-glance-item:hover { border-color: var(--primary); }
  .ops-glance-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; background: var(--primaryL); flex-shrink: 0; }
  .msg-bubble.wide { max-width: min(900px, 100%); width: 100%; }
  .msg.assistant-wide { max-width: 960px; width: 100%; }

  /* ── Loggly-style logs explorer ── */
  .lgly { display: flex; flex-direction: column; background: var(--surface); border: 1.5px solid var(--border); border-radius: var(--radius2); overflow: hidden; box-shadow: var(--shadow); }
  .lgly.embedded { border-radius: 11px; }
  .lgly-tabs { display: flex; align-items: stretch; gap: 0; background: var(--bg-2); border-bottom: 1px solid var(--border); padding: 0 8px 0 12px; }
  .lgly-tab { font-size: 12px; font-weight: 600; padding: 10px 14px; color: var(--text2); border-bottom: 2px solid transparent; margin-bottom: -1px; transition: all 0.15s; }
  .lgly-tab:hover { color: var(--primary); }
  .lgly-tab.active { background: var(--surface); color: var(--primary); border-bottom-color: var(--primary); border-radius: 6px 6px 0 0; }
  .lgly-tab-new { font-size: 16px; font-weight: 700; color: var(--muted); padding: 8px 12px; }
  .lgly-search-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 12px 14px; background: var(--surface); border-bottom: 1px solid var(--border); }
  .lgly-source, .lgly-timerange { font-size: 12px; padding: 8px 10px; border: 1.5px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--text); }
  .lgly-query { flex: 1; min-width: 180px; font-size: 13px; padding: 8px 12px; border: 1.5px solid var(--border); border-radius: 6px; background: #fff; color: var(--text); }
  .lgly-query:focus { border-color: var(--primary); outline: none; }
  .lgly-search-btn { font-size: 13px; font-weight: 700; padding: 8px 18px; border-radius: 6px; background: var(--primary); color: #fff; box-shadow: 0 2px 6px rgba(255,91,46,0.22); transition: background 0.15s; }
  .lgly-search-btn:hover { background: var(--primary2); }
  .lgly-main { display: flex; min-height: 520px; background: var(--bg); }
  .lgly-main.compact { min-height: 360px; flex-direction: column; }
  .lgly-field-explorer { width: 240px; flex-shrink: 0; background: #F0F3F8; border-right: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
  .lgly-fe-head { display: flex; align-items: center; justify-content: space-between; padding: 12px 12px 8px; border-bottom: 1px solid var(--border); background: #E8ECF3; }
  .lgly-fe-title { font-size: 12px; font-weight: 700; color: var(--text); }
  .lgly-fe-icon { font-size: 14px; color: var(--muted); }
  .lgly-fe-body { display: flex; flex: 1; min-height: 0; overflow: hidden; }
  .lgly-fe-fields { width: 96px; border-right: 1px solid var(--border); overflow-y: auto; padding: 6px 0; }
  .lgly-fe-field { display: flex; align-items: center; gap: 4px; width: 100%; text-align: left; padding: 7px 10px; font-size: 11px; font-weight: 600; color: var(--text2); }
  .lgly-fe-field:hover { background: var(--primaryL); color: var(--primary); }
  .lgly-fe-field.active { background: var(--surface); color: var(--primary); border-left: 2px solid var(--primary); }
  .lgly-fe-folder { font-size: 9px; opacity: 0.6; }
  .lgly-fe-values { flex: 1; overflow-y: auto; padding: 6px 0; background: var(--surface); }
  .lgly-fe-values-head { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); padding: 6px 10px 8px; }
  .lgly-fe-value { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; width: 100%; padding: 6px 10px; text-align: left; border: none; background: none; cursor: pointer; }
  .lgly-fe-value:hover { background: var(--primaryL); }
  .lgly-fe-value.selected { background: var(--primaryL); }
  .lgly-fe-value-name { font-family: var(--mono-f); font-size: 11px; font-weight: 600; color: var(--text); max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .lgly-fe-value-count { font-size: 10px; color: var(--muted); }
  .lgly-fe-active { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 10px; border-top: 1px solid var(--border); }
  .lgly-chip { font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px; background: var(--primaryL); color: var(--primary); border: 1px solid rgba(255,91,46,0.28); }
  .lgly-center { flex: 1; display: flex; flex-direction: column; min-width: 0; background: var(--surface); }
  .lgly-center-tabs { display: flex; gap: 0; padding: 0 14px; border-bottom: 1px solid var(--border); background: var(--bg); }
  .lgly-center-tab { font-size: 12px; font-weight: 700; padding: 10px 14px; color: var(--text2); border-bottom: 2px solid transparent; margin-bottom: -1px; }
  .lgly-center-tab.active { color: var(--primary); border-bottom-color: var(--primary); background: var(--surface); }
  .lgly-timeline { padding: 12px 16px 10px; border-bottom: 1px solid var(--border); }
  .lgly-timeline-meta { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  .lgly-event-count { font-family: var(--display); font-weight: 800; font-size: 18px; color: var(--text); }
  .lgly-event-range { font-size: 11px; color: var(--muted); }
  .lgly-timeline-svg { width: 100%; height: 150px; display: block; }
  .lgly.embedded .lgly-timeline-svg { height: 110px; }
  .lgly-grid { stroke: var(--border); stroke-width: 1; }
  .lgly-axis-label { fill: var(--muted); font-size: 9px; font-family: var(--mono-f); }
  .lgly-legend { display: flex; flex-wrap: wrap; gap: 8px 14px; margin-top: 8px; justify-content: center; }
  .lgly-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: var(--text2); }
  .lgly-legend-swatch { width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }
  .lgly-chart-placeholder { padding: 40px; text-align: center; }
  .lgly-event-view { flex: 1; display: flex; flex-direction: column; min-height: 0; }
  .lgly-event-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 14px; border-bottom: 1px solid var(--border); background: var(--bg); }
  .lgly-ev-title { font-size: 12px; font-weight: 700; color: var(--text); margin-right: 4px; }
  .lgly-tool-btn { font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 5px; border: 1px solid var(--border); background: var(--surface); color: var(--text2); }
  .lgly-tool-btn:hover { border-color: var(--primary); color: var(--primary); }
  .lgly-ev-count { margin-left: auto; font-size: 11px; color: var(--muted); font-weight: 600; }
  .lgly-event-list { flex: 1; overflow-y: auto; max-height: 380px; }
  .lgly.embedded .lgly-event-list { max-height: 220px; }
  .lgly-event { padding: 8px 14px; border-bottom: 1px solid var(--border); cursor: pointer; line-height: 1.55; transition: background 0.12s; }
  .lgly-event-toggle { display:block; width:100%; text-align:left; background:transparent; color:inherit; border:0; padding:0; font:inherit; line-height:inherit; cursor:pointer; }
  .lgly-event:hover { background: #F5F8FF; }
  .lgly-event.expanded { background: var(--primaryL); }
  .lgly-event-ts { font-size: 11px; font-weight: 700; color: var(--text); margin-right: 8px; white-space: nowrap; }
  .lgly-json-badge { display: inline-block; font-size: 10px; font-weight: 700; color: #fff; background: var(--primary); padding: 1px 6px; border-radius: 3px; margin-right: 8px; vertical-align: middle; }
  .lgly-event-body { font-size: 11.5px; color: var(--text2); word-break: break-word; }
  .lgly-key { color: var(--primary); font-weight: 600; }
  .lgly-val { color: var(--text); }
  .lgly-event-extra { margin-top: 8px; padding: 8px 10px; background: var(--surface); border: 1px solid var(--border); border-radius: 6px; font-size: 11px; line-height: 1.6; }
  .lgly-empty { padding: 28px; text-align: center; color: var(--muted); font-size: 13px; }
  .lgly-domain-bar { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-bottom: 1px solid var(--border); background: var(--bg); }
  .lgly-domain-tab { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 12px 14px; text-align: left; border-right: 1px solid var(--border); transition: background 0.15s; }
  .lgly-domain-tab:last-child { border-right: none; }
  .lgly-domain-tab:hover { background: var(--primaryL); }
  .lgly-domain-tab.active { background: var(--surface); box-shadow: inset 0 -2px 0 var(--primary); }
  .lgly-domain-icon { font-size: 16px; }
  .lgly-domain-label { font-size: 13px; font-weight: 700; color: var(--text); }
  .lgly-domain-hint { font-size: 10px; color: var(--muted); }
  .lgly-sre-strip { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px 14px; background: #FEF2F2; border-bottom: 1px solid #FECACA; }
  .lgly-sre-metric { min-width: 120px; padding: 8px 12px; background: var(--surface); border: 1px solid var(--border); border-radius: 8px; }
  .lgly-sre-metric-wide { flex: 1; min-width: 180px; }
  .lgly-sre-label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: var(--muted); margin-bottom: 4px; }
  .lgly-sre-value { font-family: var(--display); font-weight: 800; font-size: 18px; color: var(--text); }
  .lgly-sre-sub { display: block; font-size: 10px; color: var(--muted); margin-top: 2px; }
  .lgly-sre-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .lgly-chip-danger { background: #FEE2E2; color: #B91C1C; border-color: #FECACA; }
  .lgly-sre-sev { display: flex; flex-wrap: wrap; gap: 8px; font-size: 11px; font-weight: 700; }
  .lgly-sev-critical { color: #B91C1C; }
  .lgly-sev-high { color: #EA580C; }
  .lgly-sev-medium { color: #D97706; }
  .ops-logs-link-row { margin-bottom: 8px; }
  @media (max-width: 900px) {
    .lgly-domain-bar { grid-template-columns: repeat(2, 1fr); }
  }
    .lgly-main:not(.compact) { flex-direction: column; }
    .lgly-field-explorer { width: 100%; max-height: 200px; border-right: none; border-bottom: 1px solid var(--border); }
  }

  .ops-glance-clickable { cursor: pointer; }
  .ops-glance-clickable:hover { border-color: var(--primary); transform: translateY(-1px); }
  .ops-glance-spark { display: flex; align-items: flex-end; gap: 2px; height: 28px; margin-top: 6px; }
  .ops-glance-spark-bar { flex: 1; border-radius: 2px 2px 0 0; min-height: 3px; background: linear-gradient(180deg, var(--danger), #F87171); opacity: 0.8; }

  /* ── Page header ── */
  .console-page { animation: pageIn 0.25s ease both; }
  @keyframes pageIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
  .page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 22px; gap: 16px; flex-wrap: wrap; }
  .page-header-main { min-width: 0; flex: 1; }
  .page-title-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .page-title { font-family: var(--display); font-weight: 800; font-size: 20px; color: var(--text); margin: 0; }
  .page-sub { font-size: 13px; color: var(--text2); margin: 6px 0 0; line-height: 1.45; }
  .page-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex-shrink: 0; }
  .page-badge { font-family: var(--mono-f); font-size: 10px; font-weight: 600; letter-spacing: 0.6px; text-transform: uppercase; padding: 3px 9px; border-radius: 999px; border: 1px solid transparent; }
  .page-badge-live { color: var(--success); background: var(--successL); border-color: #BBF7D0; }
  .page-badge-demo { color: var(--warning); background: var(--warningL); border-color: #FDE68A; }
  .page-sync { font-size: 11px; color: var(--muted); font-weight: 600; }

  /* ── Mobile bottom nav ── */
  .bottom-nav {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
    background: var(--surface); border-top: 1.5px solid var(--border);
    padding: 8px 0 env(safe-area-inset-bottom, 8px);
    box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
  }
  .bottom-nav-inner { display: flex; justify-content: space-around; }
  .bn-item { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 4px 8px; border-radius: 10px; cursor: pointer; min-width: 56px; }
  .bn-item.active { color: var(--primary); }
  .bn-item:not(.active) { color: var(--muted); }
  .bn-icon { font-size: 20px; }
  .bn-label { font-size: 10px; font-weight: 600; }
  @media (max-width: 768px) {
    .bottom-nav { display: block; }
    .content { padding-bottom: 76px; }
  }
  @media (max-width: 480px) {
    .content { padding-bottom: 84px; }
  }

  /* ── ITSM (adapted from Veltacore patterns) ── */
  .itsm-row-click { cursor: pointer; }
  .itsm-row-click:hover td { background: #F5F8FF !important; }
  .itsm-sla-bar { height: 8px; background: var(--border); border-radius: 99px; overflow: hidden; }
  .itsm-sla-fill { height: 100%; border-radius: 99px; transition: width 0.5s ease; }
  .itsm-kv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; }
  @media (max-width: 560px) { .itsm-kv-grid { grid-template-columns: 1fr; } }
  .itsm-kv-k { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: var(--muted); margin-bottom: 3px; }
  .itsm-kv-v { font-size: 13px; font-weight: 600; }
  .itsm-timeline-row { display: flex; align-items: flex-start; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border); }
  .itsm-timeline-row:last-child { border-bottom: none; }
  .itsm-ci-active { border-color: var(--primary) !important; box-shadow: 0 0 0 1px var(--primary); }
  .itsm-ctl { position: relative; padding-top: 4px; overflow: hidden; }
  .itsm-ctl-axis { position: relative; height: 16px; margin-bottom: 8px; margin-left: 92px; margin-right: 8px; }
  .itsm-ctl-axis span { position: absolute; transform: translateX(-50%); font-size: 10px; color: var(--muted); font-family: var(--mono-f); }
  .itsm-ctl-grid { position: relative; }
  .itsm-ctl-lane { display: grid; grid-template-columns: 92px 1fr; align-items: center; height: 32px; margin-bottom: 6px; }
  .itsm-ctl-lane-label { font-size: 11px; color: var(--text2); font-weight: 600; padding-right: 12px; }
  .itsm-ctl-lane-track { position: relative; height: 26px; background: var(--bg); border-radius: 6px; border: 1px solid var(--border); }
  .itsm-ctl-event { position: absolute; top: 3px; bottom: 3px; padding: 0 8px; border: 1px solid var(--primary); border-radius: 4px; display: flex; align-items: center; font-size: 10px; font-weight: 600; color: var(--primary); background: var(--primaryL); min-width: 8px; white-space: nowrap; overflow: hidden; }
  .itsm-ctl-event.emergency { border-color: var(--danger); color: var(--danger); background: var(--dangerL); }
  .itsm-ctl-now { position: absolute; top: -4px; bottom: -4px; width: 0; border-left: 1.5px dashed var(--danger); pointer-events: none; z-index: 2; }
  .itsm-ctl-now span { position: absolute; top: -2px; left: 4px; font-size: 9px; font-weight: 700; color: var(--danger); background: var(--surface); padding: 0 4px; border-radius: 3px; font-family: var(--mono-f); }
  .lgly-incident-link { margin-top: 8px; }

  /* ── L2 enterprise knowledge chat ── */
  .chat-knowledge-banner {
    display: flex; align-items: flex-start; gap: 10px; flex-wrap: wrap;
    padding: 10px 16px;
    background: linear-gradient(90deg, rgba(255,241,234,0.95) 0%, rgba(232,245,239,0.88) 100%);
    border-bottom: 1px solid rgba(255,91,46,0.22);
    font-size: 12px; color: var(--text2); line-height: 1.5;
  }
  .chat-knowledge-banner strong { color: var(--primary); }
  .chat-upload-link { font-weight: 700; color: var(--primary); text-decoration: underline; cursor: pointer; background: none; border: none; padding: 0; font-size: inherit; }
  .ent-panel { background: var(--surface); border: 1.5px solid var(--border); border-radius: 11px; padding: 14px 16px; }
  .ent-answer { border-color: rgba(255,91,46,0.28); background: linear-gradient(180deg, var(--card) 0%, var(--surface) 100%); }
  .ent-answer-body { font-size: 14px; line-height: 1.65; color: var(--text); white-space: pre-wrap; }
  .ent-ref-list { display: flex; flex-direction: column; gap: 10px; }
  .ent-ref-card { padding: 12px; border-radius: 9px; border: 1px solid var(--border); background: var(--bg); }
  .ent-ref-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; margin-top: 8px; }
  @media (max-width: 560px) { .ent-ref-grid { grid-template-columns: 1fr; } }
  .ent-confidence-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .ent-confidence-pct { font-size: 22px; font-weight: 800; color: var(--primary); }
  .ent-related-chips { display: flex; flex-wrap: wrap; gap: 8px; }
  .ent-doc-chip {
    display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px;
    border-radius: 99px; border: 1.5px solid rgba(255,91,46,0.28); background: var(--primaryL);
    font-size: 12px; font-weight: 600; color: var(--primary); cursor: pointer; transition: all 0.15s;
  }
  .ent-doc-chip:hover { background: var(--primaryL); transform: translateY(-1px); }
  .ent-doc-type { font-size: 10px; font-weight: 800; text-transform: uppercase; opacity: 0.75; }
  .ent-escalation { border-color: #FDE68A; background: linear-gradient(180deg, #FFFBEB 0%, var(--surface) 100%); }
  .ent-feedback {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    padding-top: 10px; margin-top: 4px; border-top: 1px dashed var(--border);
  }
  .ent-feedback-btn {
    width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid var(--border);
    background: var(--bg); font-size: 14px; transition: all 0.15s;
  }
  .ent-feedback-btn:hover, .ent-feedback-btn.active { border-color: var(--primary); background: var(--primaryL); }
  .ent-feedback-report {
    margin-left: auto; font-size: 11px; font-weight: 700; color: var(--danger);
    background: var(--dangerL); border: 1px solid #FECACA; padding: 5px 10px; border-radius: 7px;
  }
  .ent-feedback-report.reported { color: var(--success); background: var(--successL); border-color: #BBF7D0; }
  .docs-upload-banner {
    margin-bottom: 14px; padding: 14px 16px; border-radius: var(--radius2);
    border: 1.5px solid rgba(255,91,46,0.28); background: linear-gradient(135deg, var(--card), var(--primaryL));
    font-size: 13px; line-height: 1.55; color: var(--text2);
  }
  .docs-upload-banner code { font-family: var(--mono-f); font-size: 11px; background: var(--surface); padding: 2px 6px; border-radius: 4px; }

  /* ── ModelPilot auth gate ── */
  .mp-auth { display:flex; height:100vh; overflow:hidden; background:var(--bg); }
  .mp-left { width:min(480px,42vw); flex-shrink:0; background:var(--ink); color:var(--paper); display:flex; flex-direction:column; justify-content:space-between; padding:44px; position:relative; overflow:hidden; }
  .mp-glow { position:absolute; top:-140px; right:-90px; width:520px; height:520px; background:radial-gradient(circle,rgba(255,91,46,0.16) 0%,transparent 65%); pointer-events:none; }
  .mp-glow2 { position:absolute; bottom:-90px; left:-70px; width:360px; height:360px; background:radial-gradient(circle,rgba(43,76,255,0.10) 0%,transparent 65%); pointer-events:none; }
  .mp-grid { position:absolute; inset:0; background-image:linear-gradient(rgba(255,255,255,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.035) 1px,transparent 1px); background-size:56px 56px; mask-image:radial-gradient(ellipse 80% 70% at 50% 40%,black 30%,transparent 85%); -webkit-mask-image:radial-gradient(ellipse 80% 70% at 50% 40%,black 30%,transparent 85%); }
  .mp-brand { display:flex; align-items:center; gap:11px; position:relative; z-index:1; }
  .mp-logo { width:38px; height:38px; border-radius:11px; background:conic-gradient(from 210deg,var(--primary),var(--accent-2),var(--accent),var(--gold),var(--primary)); display:grid; place-items:center; position:relative; box-shadow:0 6px 18px rgba(255,91,46,.3); }
  .mp-logo::after { content:""; position:absolute; inset:2px; border-radius:9px; background:var(--ink); }
  .mp-logo span { position:relative; z-index:2; font-family:var(--display); font-style:italic; font-size:20px; color:var(--paper); }
  .mp-brand-name { font-family:var(--display); font-size:22px; letter-spacing:.3px; }
  .mp-brand-name em { font-style:italic; color:var(--primary); }
  .mp-left-main { position:relative; z-index:1; display:flex; flex-direction:column; gap:28px; margin:36px 0; flex:1; }
  .mp-left-h { font-family:var(--display); font-size:clamp(28px,3.2vw,40px); line-height:1.05; font-weight:400; letter-spacing:-.01em; }
  .mp-left-h em { color:var(--primary); font-style:italic; }
  .mp-steps { display:flex; flex-direction:column; gap:16px; }
  .mp-step { display:flex; gap:14px; align-items:flex-start; }
  .mp-step-num { width:30px; height:30px; border-radius:9px; background:rgba(255,91,46,0.12); border:1px solid rgba(255,91,46,0.28); display:flex; align-items:center; justify-content:center; font-family:var(--mono-f); font-size:11px; font-weight:600; color:var(--primary); flex-shrink:0; }
  .mp-step-title { font-size:15px; font-weight:500; color:#faf7f0; }
  .mp-step-sub { font-size:13px; color:#6f6a62; margin-top:2px; }
  .mp-pills { display:flex; flex-wrap:wrap; gap:8px; }
  .mp-pill { display:flex; align-items:center; gap:7px; padding:6px 12px; border-radius:999px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); font-family:var(--mono-f); font-size:11px; letter-spacing:.4px; color:#a9a39a; }
  .mp-dot { width:6px; height:6px; border-radius:50%; background:var(--primary); display:inline-block; }
  .mp-free { background:rgba(15,122,85,0.12); border:1px solid rgba(15,122,85,0.3); border-radius:12px; padding:14px 16px; display:flex; gap:11px; align-items:flex-start; }
  .mp-free > i { width:8px; height:8px; border-radius:50%; background:var(--accent); margin-top:6px; box-shadow:0 0 0 4px rgba(15,122,85,0.18); flex-shrink:0; }
  .mp-free-t { font-size:13.5px; color:#9fe0c0; font-weight:500; }
  .mp-free-s { font-size:11px; color:rgba(159,224,192,0.6); margin-top:3px; font-family:var(--mono-f); }
  .mp-foot { position:relative; z-index:1; font-family:var(--mono-f); font-size:11px; letter-spacing:.5px; text-transform:uppercase; color:#6f6a62; }
  .mp-foot button { color:#a9a39a; font-weight:500; border-bottom:1px solid rgba(255,255,255,0.1); font-family:inherit; font-size:inherit; letter-spacing:inherit; text-transform:inherit; }
  .mp-right { flex:1; display:flex; align-items:flex-start; justify-content:center; overflow-y:auto; padding:56px 28px; background-image:radial-gradient(ellipse 70% 50% at 50% -10%, rgba(255,91,46,.06), transparent 60%); }
  .mp-card { width:100%; max-width:444px; padding-bottom:40px; animation:mpFade .4s ease both; }
  @keyframes mpFade { from{transform:translateY(12px);opacity:0} to{transform:none;opacity:1} }
  .mp-form-h { font-family:var(--display); font-size:36px; font-weight:400; letter-spacing:-.01em; margin-bottom:6px; }
  .mp-form-sub { font-size:14.5px; color:var(--text2); margin-bottom:28px; font-weight:300; }
  .mp-form-sub button { color:var(--primary); font-weight:500; }
  .mp-progress { display:flex; align-items:center; margin-bottom:28px; gap:0; }
  .mp-prog-wrap { display:flex; align-items:center; flex:1; }
  .mp-prog-wrap:first-child { flex:0; }
  .mp-prog-step { display:flex; align-items:center; gap:8px; }
  .mp-prog-dot { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:var(--mono-f); font-size:11px; font-weight:600; }
  .mp-prog-dot.done { background:var(--primary); color:#fff; }
  .mp-prog-dot.active { background:var(--ink); color:var(--paper); }
  .mp-prog-dot.inactive { background:var(--bg-2); color:var(--muted); border:1px solid var(--border); }
  .mp-prog-label { font-family:var(--mono-f); font-size:10.5px; letter-spacing:1px; text-transform:uppercase; }
  .mp-prog-label.active { color:var(--text); font-weight:500; }
  .mp-prog-label.inactive { color:var(--muted); }
  .mp-prog-line { flex:1; height:1px; background:var(--border2); margin:0 10px; min-width:24px; }
  .mp-prog-line.done { background:var(--primary); }
  .mp-field { display:block; margin-bottom:16px; }
  .mp-field span { display:block; font-family:var(--mono-f); font-size:10.5px; font-weight:500; color:var(--text2); margin-bottom:7px; text-transform:uppercase; letter-spacing:1px; }
  .mp-field input { width:100%; padding:12px 14px; border-radius:11px; border:1px solid var(--border2); font-size:14.5px; color:var(--text); background:var(--card); transition:border-color .15s, box-shadow .15s; }
  .mp-field input:focus { border-color:var(--primary); box-shadow:0 0 0 3px rgba(255,91,46,0.12); }
  .mp-row2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .mp-pw { display:flex; gap:5px; margin-top:8px; }
  .mp-pw i { flex:1; height:3px; border-radius:2px; background:var(--border2); }
  .mp-pw i.on { background:var(--primary); }
  .mp-submit { width:100%; padding:14px; border-radius:999px; border:1px solid var(--ink); background:var(--ink); color:var(--paper); font-size:14.5px; font-weight:500; display:flex; align-items:center; justify-content:center; gap:8px; transition:all .18s; margin-top:8px; }
  .mp-submit:hover:not(:disabled) { background:var(--primary); border-color:var(--primary); color:#fff; }
  .mp-submit:disabled { opacity:.55; cursor:not-allowed; }
  .mp-back { width:100%; padding:12px; border-radius:999px; border:1px solid var(--border2); color:var(--text2); margin-top:10px; transition:all .15s; }
  .mp-back:hover { border-color:var(--ink); color:var(--ink); }
  .mp-demo { width:100%; margin-top:14px; padding:10px; font-size:13px; color:var(--text2); border-radius:999px; border:1px dashed var(--border2); }
  .mp-demo:hover { border-color:var(--primary); color:var(--primary); background:var(--primaryL); }
  .mp-terms { font-family:var(--mono-f); font-size:10.5px; color:var(--muted); text-align:center; margin-top:16px; line-height:1.7; letter-spacing:.3px; }
  .mp-industries { display:grid; grid-template-columns:1fr 1fr; gap:9px; margin-bottom:16px; }
  .mp-ind { padding:13px 14px; border-radius:11px; border:1px solid var(--border2); background:var(--card); text-align:left; font-size:14px; color:var(--text2); transition:all .15s; }
  .mp-ind:hover, .mp-ind.selected { border-color:var(--primary); background:var(--primaryL); color:var(--primary); font-weight:500; }
  .mp-success { text-align:center; padding:24px 0; }
  .mp-success-icon { width:78px; height:78px; border-radius:50%; background:var(--primaryL); border:1px solid rgba(255,91,46,0.3); display:flex; align-items:center; justify-content:center; margin:0 auto 26px; }
  .mp-success-icon svg { width:34px; height:34px; stroke:var(--primary); fill:none; stroke-width:2; }
  .mp-success h3 { font-family:var(--display); font-size:30px; font-weight:400; margin-bottom:12px; }
  .mp-success p { color:var(--text2); }
  .mp-err { color:var(--danger); font-size:12px; margin:10px 0; }
  .mp-spin { width:17px; height:17px; border:2px solid rgba(250,247,240,0.35); border-top-color:var(--paper); border-radius:50%; animation:mpSpin .8s linear infinite; }
  @keyframes mpSpin { to { transform:rotate(360deg); } }
  .mp-slide { animation:mpSlide .32s ease both; }
  @keyframes mpSlide { from{transform:translateX(18px);opacity:0} to{transform:none;opacity:1} }
  @media (max-width:768px) { .mp-left { display:none; } .mp-right { padding:32px 18px; } }
  .mp-back-link { font-family:var(--mono-f); font-size:11px; letter-spacing:.8px; text-transform:uppercase; color:var(--text2); margin-bottom:18px; display:inline-flex; }
  .mp-back-link:hover { color:var(--primary); }
  .mp-left-desc { font-size:14px; color:#6f6a62; line-height:1.7; font-weight:300; max-width:360px; }
  .mp-mini { background:rgba(255,255,255,0.03); border-radius:14px; border:1px solid rgba(255,255,255,0.1); overflow:hidden; margin-top:8px; }
  .mp-mini-bar { background:rgba(0,0,0,0.25); padding:10px 14px; display:flex; gap:6px; border-bottom:1px solid rgba(255,255,255,0.1); }
  .mp-mini-bar i { width:8px; height:8px; border-radius:50%; display:block; }
  .mp-mini-body { padding:16px; }
  .mp-mini-kpis { display:flex; gap:8px; }
  .mp-mini-kpi { flex:1; background:rgba(255,255,255,0.04); border-radius:8px; padding:10px 12px; border:1px solid rgba(255,255,255,0.1); }
  .mp-mini-l { font-family:var(--mono-f); font-size:9px; color:#6f6a62; text-transform:uppercase; letter-spacing:.5px; margin-bottom:3px; }
  .mp-mini-v { font-size:18px; color:#faf7f0; letter-spacing:-0.6px; }
  .mp-mini-t { font-family:var(--mono-f); font-size:9px; color:var(--accent); margin-top:2px; }

  /* ── Landing page (ModelPilot innovative colour) ── */
  .lp { min-height:100vh; background:var(--bg); color:var(--text); position:relative; overflow-x:hidden;
    background-image:radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255,91,46,.12), transparent 60%),
      radial-gradient(ellipse 60% 40% at 92% 18%, rgba(43,76,255,.08), transparent 55%),
      radial-gradient(ellipse 70% 50% at 8% 85%, rgba(15,122,85,.08), transparent 60%),
      radial-gradient(ellipse 50% 40% at 70% 70%, rgba(124,58,237,.05), transparent 55%);
    background-attachment:fixed; }
  .lp ::selection { background:var(--primary); color:#fff; }
  .lp-grain { position:fixed; inset:0; pointer-events:none; z-index:100; opacity:.28; mix-blend-mode:multiply;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.6 0 0 0 0 0.55 0 0 0 0 0.45 0 0 0 0.09 0'/></filter><rect width='240' height='240' filter='url(%23n)'/></svg>"); }
  .lp-grid { position:fixed; inset:0; pointer-events:none; z-index:0;
    background-image:linear-gradient(rgba(14,17,22,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(14,17,22,0.07) 1px, transparent 1px);
    background-size:80px 80px; mask-image:radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 80%); }
  .lp-nav { position:fixed; top:0; left:0; right:0; z-index:50; display:flex; align-items:center; justify-content:space-between; gap:16px; padding:20px 40px;
    backdrop-filter:blur(18px); background:rgba(250,247,240,.86); border-bottom:1px solid var(--border); }
  .lp-logo { display:flex; align-items:center; gap:12px; background:none; border:none; cursor:pointer; padding:0; }
  .lp-mark { width:38px; height:38px; border-radius:11px; background:conic-gradient(from 210deg, var(--primary), var(--accent-2), var(--accent), var(--plum), var(--gold), var(--primary)); display:grid; place-items:center; position:relative; box-shadow:0 8px 22px rgba(255,91,46,.28); }
  .lp-mark::after { content:""; position:absolute; inset:2px; border-radius:9px; background:var(--paper); }
  .lp-mark span { position:relative; z-index:2; font-family:var(--display); font-style:italic; font-size:19px; color:var(--ink); }
  .lp-name { font-family:var(--display); font-size:24px; letter-spacing:.3px; color:var(--text); }
  .lp-name em { font-style:italic; color:var(--primary); }
  .lp-links { display:flex; gap:32px; font-family:var(--mono-f); font-size:11px; text-transform:uppercase; letter-spacing:1.5px; }
  .lp-links a { color:var(--text2); position:relative; transition:color .25s; }
  .lp-links a::after { content:""; position:absolute; left:0; right:0; bottom:-6px; height:1px; background:var(--primary); transform:scaleX(0); transform-origin:left; transition:transform .25s; }
  .lp-links a:hover { color:var(--text); }
  .lp-links a:hover::after { transform:scaleX(1); }
  .lp-nav-actions { display:flex; align-items:center; gap:10px; }
  .lp-cta { font-family:var(--mono-f); font-size:11px; letter-spacing:1.5px; text-transform:uppercase; padding:10px 20px; border:1px solid var(--ink); border-radius:999px; background:var(--ink); color:var(--paper); display:inline-flex; align-items:center; gap:8px; transition:all .3s; }
  .lp-cta:hover { background:var(--primary); border-color:var(--primary); color:#fff; box-shadow:0 8px 24px rgba(255,91,46,.25); }
  .lp-ghost { font-family:var(--mono-f); font-size:11px; letter-spacing:1.5px; text-transform:uppercase; padding:10px 18px; border:1px solid var(--border2); border-radius:999px; background:transparent; color:var(--text); transition:all .25s; }
  .lp-ghost:hover { border-color:var(--ink); background:var(--ink); color:var(--paper); }
  .lp-ghost-lg { padding:16px 24px; font-size:12px; }
  .lp-hero { position:relative; z-index:1; min-height:100vh; padding:160px 40px 80px; max-width:1400px; margin:0 auto; display:flex; flex-direction:column; justify-content:center; overflow:hidden; }
  .lp-tag { font-family:var(--mono-f); font-size:11px; letter-spacing:3px; text-transform:uppercase; color:var(--primary); margin-bottom:32px; display:flex; align-items:center; gap:14px; animation:lpFadeUp .9s .15s both; }
  .lp-tag::before { content:""; width:40px; height:1px; background:var(--primary); }
  .lp-hero h1 { font-family:var(--display); font-size:clamp(48px, 9vw, 120px); line-height:.92; letter-spacing:-.03em; font-weight:400; margin-bottom:40px; }
  .lp-line { display:block; overflow:visible; }
  .lp-line span { display:inline-block; animation:lpReveal 1.1s cubic-bezier(.2,.9,.2,1) both; }
  .lp-line:nth-child(1) span { animation-delay:.3s; }
  .lp-line:nth-child(2) span { animation-delay:.45s; }
  .lp-line:nth-child(3) span { animation-delay:.6s; }
  @keyframes lpReveal { from { opacity:0; transform:translateY(.35em); } to { opacity:1; transform:none; } }
  @keyframes lpFadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
  .lp-hero h1 em { font-style:italic; color:var(--primary); }
  .lp-hero h1 em.blue { color:var(--accent-2); }
  .lp-hero-meta { display:grid; grid-template-columns:1.35fr 1fr; gap:48px; align-items:end; animation:lpFadeUp 1s .85s both; }
  .lp-desc { font-size:19px; line-height:1.58; color:var(--text2); max-width:540px; font-weight:300; }
  .lp-desc strong { color:var(--text); font-weight:500; }
  .lp-cta-col { display:flex; flex-direction:column; gap:14px; align-items:flex-start; }
  .lp-cta-primary { display:inline-flex; align-items:center; gap:14px; padding:18px 32px; background:var(--ink); color:var(--paper); font-family:var(--mono-f); font-size:12px; letter-spacing:1.5px; text-transform:uppercase; border:none; border-radius:999px; transition:all .35s; position:relative; overflow:hidden; }
  .lp-cta-primary::before { content:""; position:absolute; inset:0; background:linear-gradient(135deg, var(--primary), #c2410c); transform:translateX(-101%); transition:transform .35s; }
  .lp-cta-primary span { position:relative; z-index:2; }
  .lp-cta-primary:hover::before { transform:translateX(0); }
  .lp-cta-primary:hover { color:#fff; box-shadow:0 12px 32px rgba(255,91,46,.3); }
  .lp-arr { width:22px; height:22px; border-radius:50%; background:var(--paper); color:var(--ink); display:grid; place-items:center; position:relative; z-index:2; transition:background .35s; }
  .lp-cta-primary:hover .lp-arr { background:#fff; }
  .lp-cta-group { display:flex; flex-wrap:wrap; gap:12px; align-items:center; }
  .lp-stats { display:flex; flex-wrap:wrap; gap:10px; align-items:center; font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--muted); margin-top:8px; }
  .lp-stats strong { color:var(--text); font-weight:600; }
  .lp-stat-sep { opacity:.4; }
  .lp-orbit { position:absolute; right:-120px; top:50%; transform:translateY(-50%); width:680px; height:680px; pointer-events:none; opacity:.7; z-index:0; }
  .lp-orbit svg { width:100%; height:100%; animation:lpSpin 120s linear infinite; }
  .lp-orbit-core { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); width:160px; height:160px; border-radius:50%;
    background:radial-gradient(circle at 30% 30%, #fff, var(--bg-2) 70%); box-shadow:inset 0 0 40px rgba(255,91,46,.25), 0 20px 80px rgba(43,76,255,.18); animation:lpPulse 4s ease-in-out infinite; }
  .lp-orbit-glyph { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); font-family:var(--display); font-size:52px; font-style:italic; color:var(--ink); z-index:2; text-align:center; line-height:1; }
  .lp-orbit-glyph em { color:var(--primary); font-style:italic; }
  .lp-orbit-chip { position:absolute; display:inline-flex; align-items:center; gap:6px; padding:8px 14px; background:rgba(250,247,240,.92); backdrop-filter:blur(8px); border:1px solid var(--border2); border-radius:999px; font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--text2); box-shadow:0 8px 20px rgba(14,17,22,.08); animation:lpFloat 5s ease-in-out infinite; }
  .lp-oc-dot { width:6px; height:6px; border-radius:50%; display:inline-block; }
  .lp-orbit-chip.orange .lp-oc-dot { background:var(--primary); }
  .lp-orbit-chip.blue .lp-oc-dot { background:var(--accent-2); }
  .lp-orbit-chip.green .lp-oc-dot { background:var(--accent); }
  .lp-orbit-chip.gold .lp-oc-dot { background:var(--gold); }
  .lp-orbit-chip.plum .lp-oc-dot { background:var(--plum); }
  .lp-oc1 { top:8%; left:6%; }
  .lp-oc2 { top:16%; right:6%; animation-delay:1s; }
  .lp-oc3 { bottom:20%; left:2%; animation-delay:2s; }
  .lp-oc4 { bottom:8%; right:12%; animation-delay:1.5s; }
  .lp-oc5 { top:46%; right:-2%; animation-delay:.5s; }
  .lp-orbit-svg { width:100%; height:100%; animation:lpSpin 120s linear infinite; }
  .lp-hero-mock { position:absolute; right:max(0px, calc((100% - 1400px)/2 + 20px)); top:50%; transform:translateY(-42%) rotate(-1deg); width:min(480px, 40vw); z-index:2; animation:lpMockFloat 6s ease-in-out infinite; }
  @keyframes lpMockFloat { 0%,100% { transform:translateY(-42%) rotate(-1deg); } 50% { transform:translateY(-44%) rotate(0deg); } }
  .lp-shot-frame { border-radius:16px; overflow:hidden; box-shadow:0 40px 100px -30px rgba(14,17,22,.35), 0 0 0 1px rgba(14,17,22,.08); background:#1a1a22; }
  .lp-shot-frame.compact { box-shadow:0 16px 40px -20px rgba(14,17,22,.25), 0 0 0 1px rgba(14,17,22,.08); }
  .lp-shot-chrome { display:flex; align-items:center; gap:8px; padding:10px 14px; background:#1a1a22; border-bottom:1px solid rgba(255,255,255,.06); }
  .lp-mock-dot { width:10px; height:10px; border-radius:50%; }
  .lp-mock-dot.red { background:#ff5f57; }
  .lp-mock-dot.amber { background:#febc2e; }
  .lp-mock-dot.green { background:#28c840; }
  .lp-mock-url { margin-left:auto; font-family:var(--mono-f); font-size:10px; color:rgba(255,255,255,.35); letter-spacing:.5px; }
  .lp-shot-img { width:100%; height:auto; display:block; vertical-align:top; }
  .lp-shot-frame.compact .lp-shot-img { max-height:148px; object-fit:cover; object-position:top left; }
  .lp-bento-shot { margin:14px 0 10px; position:relative; z-index:1; }
  .lp-showcase-shot { animation:lpFadeUp .45s ease both; border-radius:18px; overflow:hidden; }
  .lp-gallery-section { padding-top:80px; }
  .lp-gallery { display:grid; grid-template-columns:repeat(2,1fr); gap:20px; }
  .lp-gallery-item { margin:0; }
  .lp-gallery-cap { font-family:var(--mono-f); font-size:10px; letter-spacing:1.2px; text-transform:uppercase; color:var(--muted); margin-top:10px; text-align:center; }
  @media (max-width:900px) { .lp-gallery { grid-template-columns:1fr; } }
  .lp-cap-icon { width:56px; height:56px; margin-bottom:16px; }
  .lp-cap-icon svg { width:100%; height:100%; display:block; border-radius:16px; box-shadow:0 8px 20px rgba(14,17,22,.12); }
  .lp-b-visual { position:relative; z-index:1; margin:12px 0 8px; min-height:72px; }
  .lp-bv-chat .lp-bv-window { margin-left:auto; width:min(200px, 100%); background:#0a0e14; border-radius:12px; overflow:hidden; border:1px solid rgba(255,255,255,.08); box-shadow:0 12px 32px rgba(14,17,22,.2); }
  .lp-bv-bar { height:8px; background:linear-gradient(90deg,#ff5b2e,#2b4cff,#0f7a55); opacity:.85; }
  .lp-bv-chat { padding:10px; display:flex; flex-direction:column; gap:6px; }
  .lp-bv-bubble { font-size:9px; padding:6px 8px; border-radius:8px; max-width:90%; }
  .lp-bv-bubble.u { align-self:flex-end; background:var(--primary); color:#fff; border-radius:8px 8px 2px 8px; }
  .lp-bv-bubble.b { background:rgba(255,255,255,.08); color:#eef1f6; }
  .lp-bv-logs { display:flex; align-items:flex-end; gap:5px; height:64px; padding:0 4px; }
  .lp-bv-logs span { flex:1; border-radius:4px 4px 0 0; background:linear-gradient(180deg,var(--accent-2),var(--primary)); opacity:.85; animation:lpBarGrow .8s ease-out both; transform-origin:bottom; }
  @keyframes lpBarGrow { from { transform:scaleY(0); } to { transform:scaleY(1); } }
  .lp-bv-kb { display:flex; gap:8px; justify-content:flex-end; padding-right:8px; }
  .lp-bv-doc { width:44px; height:56px; background:var(--surface); border:1px solid var(--border2); border-radius:8px; display:grid; place-items:center; font-size:20px; box-shadow:0 6px 16px rgba(14,17,22,.08); }
  .lp-bv-int { display:grid; grid-template-columns:1fr 1fr; gap:6px; width:100px; margin-left:auto; }
  .lp-bv-int-tile { font-family:var(--mono-f); font-size:9px; font-weight:700; padding:10px 6px; text-align:center; border-radius:10px; color:#fff; }
  .lp-bv-int-tile.c0 { background:linear-gradient(145deg,#0f7a55,#064e3b); }
  .lp-bv-int-tile.c1 { background:linear-gradient(145deg,#632ca6,#4c1d95); }
  .lp-bv-int-tile.c2 { background:linear-gradient(145deg,#2b4cff,#1e3a8a); }
  .lp-bv-int-tile.c3 { background:linear-gradient(145deg,#ff5b2e,#c2410c); }
  .lp-bv-itsm { display:flex; justify-content:flex-end; }
  .lp-bv-ticket { display:flex; align-items:center; gap:8px; padding:10px 14px; background:var(--dangerL); border:1px solid #fecaca; border-radius:10px; font-family:var(--mono-f); font-size:11px; font-weight:600; }
  .lp-bv-sev { background:var(--danger); color:#fff; padding:2px 6px; border-radius:4px; font-size:9px; }
  .lp-providers { position:relative; z-index:1; padding:80px 40px; max-width:1400px; margin:0 auto; text-align:center; }
  .lp-providers .lp-num { justify-content:center; }
  .lp-prov-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; margin-top:36px; }
  .lp-prov-card { padding:28px 20px; background:var(--surface); border:1px solid var(--border); border-radius:20px; transition:all .35s; text-align:left; }
  .lp-prov-card:hover { transform:translateY(-4px); box-shadow:0 20px 40px -16px rgba(14,17,22,.15); border-color:var(--border2); }
  .lp-prov-icon { width:52px; height:52px; border-radius:14px; display:grid; place-items:center; font-family:var(--display); font-size:24px; font-style:italic; color:#fff; margin-bottom:14px; box-shadow:0 8px 20px rgba(14,17,22,.15); }
  .lp-prov-card.orange .lp-prov-icon { background:linear-gradient(145deg,var(--primary),#c2410c); }
  .lp-prov-card.blue .lp-prov-icon { background:linear-gradient(145deg,var(--accent-2),#1e3a8a); }
  .lp-prov-card.green .lp-prov-icon { background:linear-gradient(145deg,var(--accent),#064e3b); }
  .lp-prov-card.plum .lp-prov-icon { background:linear-gradient(145deg,var(--plum),#5b21b6); }
  .lp-prov-name { font-family:var(--display); font-size:22px; margin-bottom:6px; }
  .lp-prov-tag { font-family:var(--mono-f); font-size:9px; letter-spacing:1.2px; text-transform:uppercase; color:var(--muted); }
  .lp-arch-section { padding-top:60px; padding-bottom:80px; }
  .lp-arch { margin-top:32px; padding:28px; background:var(--surface); border:1px solid var(--border); border-radius:22px; box-shadow:var(--shadow); }
  .lp-arch-svg { width:100%; height:auto; display:block; }
  @keyframes lpSpin { to { transform:rotate(360deg); } }
  @keyframes lpPulse { 0%,100% { box-shadow:inset 0 0 40px rgba(255,91,46,.2), 0 20px 80px rgba(43,76,255,.15); } 50% { box-shadow:inset 0 0 60px rgba(255,91,46,.35), 0 30px 100px rgba(43,76,255,.22); } }
  @keyframes lpFloat { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
  .lp-marquee { margin-top:0; padding:22px 0; border-top:1px solid var(--border); border-bottom:1px solid var(--border); overflow:hidden; position:relative; z-index:1; }
  .lp-marquee-track { display:inline-flex; gap:60px; animation:lpMarquee 32s linear infinite; font-family:var(--display); font-size:24px; font-style:italic; color:var(--text2); white-space:nowrap; }
  .lp-marquee-track span::after { content:"✦"; margin-left:60px; color:var(--primary); font-style:normal; font-size:16px; }
  @keyframes lpMarquee { to { transform:translateX(-50%); } }
  .lp-section { position:relative; z-index:1; padding:120px 40px; max-width:1400px; margin:0 auto; }
  .lp-num { font-family:var(--mono-f); font-size:11px; letter-spacing:3px; text-transform:uppercase; color:var(--text2); margin-bottom:32px; display:flex; align-items:center; gap:16px; }
  .lp-num::before { content:""; width:48px; height:1px; background:var(--primary); }
  .lp-section h2 { font-family:var(--display); font-size:clamp(36px,5.5vw,72px); line-height:1.02; font-weight:400; letter-spacing:-.02em; max-width:900px; margin-bottom:36px; }
  .lp-section h2 em { font-style:italic; color:var(--primary); }
  .lp-section h2 em.blue { color:var(--accent-2); }
  .lp-section h2 em.green { color:var(--accent); }
  .lp-manifesto { display:grid; grid-template-columns:repeat(3,1fr); gap:36px; padding-top:32px; border-top:1px solid var(--border); }
  .lp-m-card { padding:28px 0; }
  .lp-m-card .n { font-family:var(--mono-f); font-size:11px; letter-spacing:2px; margin-bottom:18px; }
  .lp-m-card.orange .n { color:var(--primary); }
  .lp-m-card.blue .n { color:var(--accent-2); }
  .lp-m-card.green .n { color:var(--accent); }
  .lp-m-card h3 { font-family:var(--display); font-size:28px; font-weight:400; margin-bottom:12px; }
  .lp-m-card p { color:var(--text2); line-height:1.65; font-size:15px; font-weight:300; }
  .lp-products { max-width:none; padding-left:max(40px, calc((100% - 1400px)/2 + 40px)); padding-right:max(40px, calc((100% - 1400px)/2 + 40px)); background:linear-gradient(180deg, transparent, rgba(43,76,255,.05), transparent); }
  .lp-products-head { display:grid; grid-template-columns:1.2fr 1fr; gap:40px; align-items:end; margin-bottom:48px; max-width:1400px; }
  .lp-products-head p { color:var(--text2); font-size:16px; line-height:1.6; font-weight:300; }
  .lp-bento { display:grid; grid-template-columns:repeat(12,1fr); gap:18px; max-width:1400px; }
  .lp-bento-card { position:relative; border:1px solid var(--border); border-radius:22px; padding:32px; background:var(--surface); overflow:hidden; cursor:pointer; transition:all .4s cubic-bezier(.2,.9,.2,1); min-height:280px; display:flex; flex-direction:column; box-shadow:0 2px 8px rgba(14,17,22,.04); }
  .lp-bento-card:hover { transform:translateY(-4px); border-color:var(--border2); box-shadow:0 24px 50px -20px rgba(14,17,22,.16); }
  .lp-bento-card::after { content:""; position:absolute; top:-30%; right:-25%; width:280px; height:280px; border-radius:50%; pointer-events:none; opacity:.75; transition:opacity .4s; }
  .lp-bento-card:hover::after { opacity:1; }
  .lp-b-orange::after { background:radial-gradient(circle, rgba(255,91,46,.14), transparent 60%); }
  .lp-b-blue::after { background:radial-gradient(circle, rgba(43,76,255,.12), transparent 60%); }
  .lp-b-green::after { background:radial-gradient(circle, rgba(15,122,85,.12), transparent 60%); }
  .lp-b-plum::after { background:radial-gradient(circle, rgba(124,58,237,.12), transparent 60%); }
  .lp-b-gold::after { background:radial-gradient(circle, rgba(138,109,46,.12), transparent 60%); }
  .lp-b-badge { position:absolute; top:20px; right:20px; font-family:var(--mono-f); font-size:9px; letter-spacing:1.5px; text-transform:uppercase; padding:5px 10px; border-radius:999px; background:rgba(255,91,46,.08); color:var(--primary); border:1px solid rgba(255,91,46,.22); z-index:1; }
  .lp-b-blue .lp-b-badge { background:rgba(43,76,255,.08); color:var(--accent-2); border-color:rgba(43,76,255,.22); }
  .lp-b-num { font-family:var(--mono-f); font-size:11px; color:var(--muted); letter-spacing:2px; margin-bottom:14px; position:relative; z-index:1; }
  .lp-bento-card h3 { font-family:var(--display); font-size:clamp(28px,3vw,38px); line-height:1; font-weight:400; margin-bottom:10px; position:relative; z-index:1; }
  .lp-bento-card h3 em { font-style:italic; color:var(--primary); }
  .lp-b-blue h3 em { color:var(--accent-2); }
  .lp-b-green h3 em { color:var(--accent); }
  .lp-b-plum h3 em { color:var(--plum); }
  .lp-b-gold h3 em { color:var(--gold); }
  .lp-b-desc { color:var(--text2); font-size:14.5px; line-height:1.6; font-weight:300; max-width:420px; flex:1; position:relative; z-index:1; }
  .lp-b-meta { display:flex; justify-content:space-between; align-items:center; margin-top:20px; padding-top:16px; border-top:1px solid var(--border); font-family:var(--mono-f); font-size:10px; letter-spacing:1px; text-transform:uppercase; color:var(--muted); position:relative; z-index:1; }
  .lp-b-arrow { width:32px; height:32px; border-radius:50%; border:1px solid var(--border2); display:grid; place-items:center; transition:all .35s; }
  .lp-bento-card:hover .lp-b-arrow { background:var(--primary); border-color:var(--primary); color:#fff; transform:rotate(-45deg); }
  .lp-b-hero { grid-column:span 7; min-height:360px; }
  .lp-b-med { grid-column:span 5; }
  .lp-b-third { grid-column:span 4; min-height:240px; }
  .lp-b-half { grid-column:span 8; }
  .lp-model-chips { display:flex; flex-direction:column; gap:8px; align-items:flex-end; margin:16px 0; position:relative; z-index:1; }
  .lp-model-chip { display:inline-flex; align-items:center; gap:7px; padding:7px 12px; background:rgba(250,247,240,.85); backdrop-filter:blur(8px); border:1px solid var(--border2); border-radius:999px; font-family:var(--mono-f); font-size:10px; color:var(--text2); animation:lpFloat 6s ease-in-out infinite; }
  .lp-model-chip i { width:6px; height:6px; border-radius:50%; background:var(--primary); display:inline-block; }
  .lp-model-chip:nth-child(2) i { background:var(--accent-2); animation-delay:1s; }
  .lp-model-chip:nth-child(3) i { background:var(--accent); }
  .lp-model-chip:nth-child(4) i { background:var(--plum); }
  .lp-agent-pills { display:flex; flex-wrap:wrap; gap:7px; margin:12px 0; position:relative; z-index:1; }
  .lp-agent-pill { padding:5px 11px; background:rgba(255,91,46,.06); border:1px solid rgba(255,91,46,.2); border-radius:999px; font-family:var(--mono-f); font-size:9px; color:var(--primary); letter-spacing:1px; text-transform:uppercase; }
  .lp-showcase { position:relative; z-index:1; padding:120px 40px; background:linear-gradient(180deg, var(--surface), var(--bg)); border-top:1px solid var(--border); border-bottom:1px solid var(--border); }
  .lp-showcase-inner { max-width:1400px; margin:0 auto; }
  .lp-showcase-head { display:grid; grid-template-columns:1fr 1fr; gap:40px; align-items:end; margin-bottom:36px; }
  .lp-showcase-head p { color:var(--text2); font-size:16px; line-height:1.6; font-weight:300; }
  .lp-tabs { display:flex; gap:8px; margin-bottom:28px; border-bottom:1px solid var(--border); flex-wrap:wrap; }
  .lp-tab { padding:12px 22px; border:none; background:transparent; font-family:var(--mono-f); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:var(--muted); border-bottom:2px solid transparent; margin-bottom:-1px; transition:color .25s; cursor:pointer; }
  .lp-tab.active { color:var(--text); border-bottom-color:var(--primary); }
  .lp-tab:hover { color:var(--text); }
  .lp-stage { display:none; border-radius:28px; overflow:hidden; padding:36px; min-height:380px; color:var(--paper);
    background:radial-gradient(ellipse 80% 60% at 30% 30%, rgba(255,91,46,.14), transparent 60%), radial-gradient(ellipse 60% 80% at 80% 70%, rgba(124,58,237,.12), transparent 60%), linear-gradient(145deg, #1a1a1f, #0a0a0e); }
  .lp-stage.active { display:flex; flex-direction:column; animation:lpFadeUp .5s ease both; }
  .lp-stage-head { display:flex; justify-content:space-between; align-items:flex-start; gap:20px; margin-bottom:24px; }
  .lp-stage-brand { font-family:var(--display); font-size:36px; line-height:1; }
  .lp-stage-brand em { font-style:italic; color:#ffb185; }
  .lp-stage-tag { font-family:var(--mono-f); font-size:10px; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,.45); text-align:right; }
  .lp-stage-chat { display:flex; flex-direction:column; gap:10px; flex:1; }
  .lp-sc-msg { padding:10px 14px; max-width:88%; border-radius:14px; font-size:13px; line-height:1.5; }
  .lp-sc-msg.bot { background:rgba(255,255,255,.06); color:#eef1f6; border-radius:14px 14px 14px 4px; }
  .lp-sc-msg.user { background:linear-gradient(135deg, var(--primary), #c2410c); color:#fff; align-self:flex-end; border-radius:14px 14px 4px 14px; }
  .lp-sc-panel { padding:12px 14px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); border-radius:12px; font-size:13px; color:#eef1f6; }
  .lp-sc-panel.blue { border-color:rgba(43,76,255,.35); background:rgba(43,76,255,.08); }
  .lp-sc-panel.green { border-color:rgba(15,122,85,.35); background:rgba(15,122,85,.08); }
  .lp-sc-tag { font-family:var(--mono-f); font-size:9px; letter-spacing:1px; text-transform:uppercase; color:#86efac; margin-left:8px; }
  .lp-stage-kpis { display:flex; flex-wrap:wrap; gap:14px; }
  .lp-stage-kpi { min-width:140px; padding:16px 18px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.1); border-radius:14px; }
  .lp-stage-kpi span { display:block; font-family:var(--mono-f); font-size:9px; letter-spacing:1.5px; text-transform:uppercase; color:rgba(255,255,255,.4); margin-bottom:6px; }
  .lp-stage-kpi strong { font-family:var(--display); font-size:28px; color:#fff; font-weight:400; }
  .lp-stage-kpi.orange strong { color:#ffb185; }
  .lp-stage-kpi.blue strong { color:#93c5fd; }
  .lp-stage-kpi.green strong { color:#86efac; }
  .lp-deploy-sub { color:var(--text2); font-size:15px; }
  .lp-deploy-sub code { font-family:var(--mono-f); font-size:12px; background:var(--bg-2); padding:3px 10px; border-radius:6px; border:1px solid var(--border); }
  .lp-foot { position:relative; z-index:1; border-top:1px solid var(--border); padding:36px 40px; display:flex; flex-wrap:wrap; gap:20px; align-items:center; justify-content:space-between; max-width:1400px; margin:0 auto; }
  .lp-foot-links { display:flex; gap:18px; }
  .lp-foot-links button { font-family:var(--mono-f); font-size:11px; letter-spacing:1px; text-transform:uppercase; color:var(--text2); }
  .lp-foot-links button:hover { color:var(--primary); }
  .lp-foot > p { width:100%; font-family:var(--mono-f); font-size:11px; color:var(--muted); letter-spacing:.4px; }
  @media (max-width:1100px) {
    .lp-b-hero, .lp-b-med, .lp-b-third, .lp-b-half { grid-column:span 12; }
    .lp-orbit, .lp-hero-mock { display:none; }
    .lp-prov-grid { grid-template-columns:repeat(2,1fr); }
  }
  @media (max-width:900px) {
    .lp-links { display:none; }
    .lp-hero { padding:120px 20px 60px; }
    .lp-hero-meta, .lp-manifesto, .lp-products-head, .lp-showcase-head { grid-template-columns:1fr; }
    .lp-section, .lp-nav, .lp-foot, .lp-showcase { padding-left:20px; padding-right:20px; }
    .lp-products { padding-left:20px; padding-right:20px; }
  }

  .btn-primary { background: var(--ink); color: var(--paper); box-shadow: 0 2px 8px rgba(14,17,22,0.18); border: 1px solid var(--ink); }
  .btn-primary:hover { background: var(--primary); border-color: var(--primary); color: #fff; }
  .send-btn { background: var(--ink); box-shadow: 0 2px 8px rgba(14,17,22,0.18); }
  .send-btn:hover { background: var(--primary); }
`;

const styleEl = document.createElement("style");
styleEl.textContent = STYLES;
document.head.appendChild(styleEl);

const NAV_ITEMS = [
  { id:"dashboards", label:"Dashboards", section:"monitor" },
  { id:"evidence", label:"Evidence viewer", section:"investigate" },
  { id:"log-analysis", label:"Log analysis", section:"investigate" },
  { id:"rca", label:"RCA reports", section:"investigate" },
  { id:"release-notes", label:"Release notes", section:"config" },
  { id:"dashboard", label:"Workspace home", icon:"📊", section:"main" },
  { id:"chat",      label:"Chat",         icon:"💬", section:"main"   },
  { id:"logs",      label:"Observability", icon:"📋", section:"main"   },
  { id:"kb",        label:"Knowledge",    icon:"🗂️", section:"main"   },
  ...(SHOW_LEARN_UI
    ? [
        { id:"learn-topics", label:"Learn · Topics", icon:"📚", section:"learn" },
        { id:"learn-k8s", label:"Learn · K8s",  icon:"☸️", section:"learn"  },
      ]
    : []),
  { id:"docs",      label:"Documents",    icon:"📄", section:"main"   },
  { id:"incidents", label:"Incidents",    icon:"🎫", section:"itsm"   },
  { id:"changes",   label:"Changes",      icon:"🔄", section:"itsm"   },
  { id:"cmdb",      label:"CMDB",         icon:"🗃️", section:"itsm"   },
  { id:"agents",    label:"Agents",       icon:"🤖", section:"ai"     },
  { id:"analytics", label:"Analytics",    icon:"📈", section:"ai"     },
  { id:"users",     label:"Users",        icon:"👥", section:"config" },
  { id:"billing",   label:"Billing",      icon:"💳", section:"config" },
  { id:"apikeys",   label:"API Keys",     icon:"🔑", section:"config" },
  { id:"integrations",label:"Integrations",icon:"🔌",section:"config" },
  { id:"audit",     label:"Audit Logs",   icon:"📋", section:"config" },
  { id:"notifs",    label:"Notifications",icon:"🔔", section:"config" },
  { id:"settings",  label:"Settings",     icon:"⚙️", section:"config" },
];
const BOTTOM_NAV = [
  { id:"dashboard", label:"Home",    icon:"📊" },
  { id:"chat",      label:"Chat",    icon:"💬" },
  { id:"kb",        label:"KBs",     icon:"🗂️" },
  { id:"agents",    label:"Agents",  icon:"🤖" },
  { id:"settings",  label:"Settings",icon:"⚙️" },
];

// ── Toast hook ───────────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type="info") => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3200);
  }, []);
  return { toasts, add };
}

function accountPresentation(account) {
  const signedIn = Boolean(getToken());
  const name = account?.name?.trim() || "";
  const email = account?.email?.trim() || "";
  const displayName = name || email || (signedIn ? "Signed-in account" : "Local preview");
  const role = account?.role
    ? `${account.role.charAt(0).toUpperCase()}${account.role.slice(1)}`
    : signedIn
      ? "Account session"
      : "Preview session";
  const source = name || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  const initials = parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : source
      ? source.slice(0, 2).toUpperCase()
      : signedIn ? "A" : "P";
  return { signedIn, displayName, email, role, initials };
}

// ── Screens ──────────────────────────────────────────────────────────────────


function LogsPage({ domain, onDomainChange, onOpenIncident, initialQuery, onInitialQueryConsumed }) {
  return (
    <PageShell
      title="Observability"
      subtitle="Application · DevOps · Platform · SRE — search, field explorer, event timeline"
      actions={
        <button className="btn btn-outline btn-sm" type="button" disabled title="Requires live log API">
          ⬇ Export
        </button>
      }
    >
      <LogsExplorer embedded={false} showHeader={false} domain={domain} onDomainChange={onDomainChange} onOpenIncident={onOpenIncident} initialQuery={initialQuery} onInitialQueryConsumed={onInitialQueryConsumed} />
    </PageShell>
  );
}






// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [theme, setTheme] = useState(() => {
    try { return readTheme(window.localStorage); } catch { return "dark"; }
  });
  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try { saveTheme(window.localStorage, next); } catch { /* Private browsing fallback. */ }
  }
  const [route, setRoute] = useState(() => getRoute());
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [route]);
  const [authed, setAuthed] = useState(() => hasSession());
  const [page, setPage] = useState(() => getConsolePage());
  const [logsDomain, setLogsDomain] = useState(() => readLogsDomain());
  const [logsInitialQuery, setLogsInitialQuery] = useState(null);
  const [itsmIncidentId, setItsmIncidentId] = useState(null);
  const [itsmChangeId, setItsmChangeId] = useState(null);
  const [chatInitialQuery, setChatInitialQuery] = useState(null);
  const [chatInitialKb, setChatInitialKb] = useState(null);
  const [account, setAccount] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(() => matchMedia("(max-width:768px)").matches);
  const sidebarRef = useRef(null);
  const menuRef = useRef(null);
  const mainContentRef = useRef(null);
  useEffect(() => {
    const publicTitles = { signin: "Sign in", signup: "Create account" };
    const title = route === "app" ? NAV_ITEMS.find(item => item.id === page)?.label : publicTitles[route];
    document.title = title ? `${title} | WeCrew Copilot` : "WeCrew Copilot";
  }, [route, page]);
  useEffect(() => {
    const media = matchMedia("(max-width:768px)");
    const update = () => {setMobileNav(media.matches); if (!media.matches) setSideOpen(false);};
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!mobileNav || !sideOpen || !sidebarRef.current) return;
    const drawer = sidebarRef.current;
    const menuButton = menuRef.current;
    const nodes = () => [...drawer.querySelectorAll('button:not([disabled]),[tabindex="0"]')].filter(el => el.getClientRects().length);
    nodes()[0]?.focus();
    const handle = e => {
      if (e.key === "Escape") {e.preventDefault();setSideOpen(false);}
      if (e.key !== "Tab") return;
      const list=nodes(), first=list[0], last=list[list.length-1];
      if (e.shiftKey && document.activeElement===first) {e.preventDefault();last?.focus();}
      if (!e.shiftKey && document.activeElement===last) {e.preventDefault();first?.focus();}
    };
    drawer.addEventListener("keydown",handle);
    return () => {drawer.removeEventListener("keydown",handle);if (menuButton?.isConnected) menuButton.focus();};
  }, [sideOpen,mobileNav]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { toasts, add:toast } = useToast();
  const identity = accountPresentation(account);

  useEffect(() => {
    if (!getToken()) {
      setAccount(null);
      return;
    }
    let active = true;
    api.me()
      .then((user) => { if (active) setAccount(user); })
      .catch(() => { if (active) setAccount(null); });
    return () => { active = false; };
  }, [authed, route]);

  useEffect(() => {
    const sync = () => {
      const nextRoute = getRoute();
      setRoute(nextRoute);
      if (nextRoute === "app") {
        // Deep links (#/integrations, #/app/chat) should open the demo console
        // without forcing a blank Sign-in screen when no JWT session exists.
        if (!hasSession()) {
          localStorage.setItem("wecrew_session", "demo");
          setAuthed(true);
        } else {
          setAuthed(true);
        }
        const hashPage = getConsolePage();
        const entry = sessionStorage.getItem("wecrew_entry_page");
        if (entry && CONSOLE_PAGES.includes(entry)) {
          setPage(entry);
          sessionStorage.removeItem("wecrew_entry_page");
          if (entry !== hashPage) goRoute("app", entry);
        } else {
          setPage(hashPage);
        }
      }
    };
    window.addEventListener("hashchange", sync);
    // Default first visit → landing (not auto-app)
    if (!window.location.hash || window.location.hash === "#") {
      goRoute("landing");
    } else {
      sync();
    }
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  function navigatePublic(next, consolePage) {
    if (next === "app" && !hasSession()) {
      localStorage.setItem("wecrew_session", "demo");
      setAuthed(true);
    }
    const entry =
      consolePage ||
      sessionStorage.getItem("wecrew_entry_page") ||
      null;
    if (next === "app") {
      if (entry) sessionStorage.removeItem("wecrew_entry_page");
      goRoute("app", entry && CONSOLE_PAGES.includes(entry) ? entry : undefined);
      setRoute("app");
      if (entry && CONSOLE_PAGES.includes(entry)) setPage(entry);
      else setPage(getConsolePage());
      return;
    }
    // Keep a selected product destination across sign-up / sign-in. Leaving
    // the auth flow for the public homepage cancels that pending destination.
    if (next === "landing") sessionStorage.removeItem("wecrew_entry_page");
    goRoute(next);
    setRoute(next);
  }

  function enterApp() {
    setAuthed(true);
    const entry = sessionStorage.getItem("wecrew_entry_page");
    if (entry) sessionStorage.removeItem("wecrew_entry_page");
    goRoute("app", entry && CONSOLE_PAGES.includes(entry) ? entry : undefined);
    setRoute("app");
    if (entry && CONSOLE_PAGES.includes(entry)) setPage(entry);
    else setPage(getConsolePage());
  }

  function signOut() {
    clearSession();
    setAccount(null);
    setAuthed(false);
    setUserMenuOpen(false);
    setSideOpen(false);
    goRoute("landing");
    setRoute("landing");
    toast("Signed out", "info");
  }

  if (route === "signup") {
    return <SignUpPage entryPage={sessionStorage.getItem("wecrew_entry_page")} onSuccess={enterApp} onNavigate={navigatePublic} />;
  }
  if (route === "signin") {
    return <SignInPage entryPage={sessionStorage.getItem("wecrew_entry_page")} onSuccess={enterApp} onNavigate={navigatePublic} />;
  }
  if (route !== "app") {
    return (
      <LandingPage
        authed={Boolean(getToken())}
        onSignOut={signOut}
        onNavigate={(r, consolePage) => {
          if (r === "app" && !authed) {
            localStorage.setItem("wecrew_session", "demo");
            setAuthed(true);
          }
          navigatePublic(r, consolePage);
        }}
      />
    );
  }

  if (!authed) {
    return <SignInPage onSuccess={enterApp} onNavigate={navigatePublic} />;
  }

  function nav(id, opts = {}) {
    if (id === "logs") setLogsInitialQuery(opts.query ?? null);
    if (opts.domain) {
      setLogsDomain(opts.domain);
      persistLogsDomain(opts.domain);
    }
    if (opts.incidentId) setItsmIncidentId(opts.incidentId);
    else if (id !== "incidents") setItsmIncidentId(null);
    setItsmChangeId(opts.changeId ?? null);
    if (opts.chatQuery) setChatInitialQuery(opts.chatQuery);
    if (opts.kbId) setChatInitialKb(opts.kbId);
    else if (id !== "chat") setChatInitialKb(null);
    setPage(id);
    setSideOpen(false);
    // Keep URL in sync so Integrations / Chat deep links work on refresh
    if (CONSOLE_PAGES.includes(id)) goRoute("app", id);
  }

  function openUpgradeRunbook(prompt) {
    nav("chat", { chatQuery: prompt });
    toast("Opened upgrade runbook in Chat", "info");
  }

  function openLogsDomain(domain) {
    nav("logs", { domain });
  }

  function openIncident(incidentId, opts = {}) {
    nav("incidents", { incidentId, ...opts });
  }

  function openIncidentLogs(domain, query) {
    setLogsDomain(domain);
    persistLogsDomain(domain);
    nav("logs", { domain, query });
    toast(`Opened related sample logs · ${domain}`, "info");
  }

  function openK8sChat(prompt) {
    nav("chat", { chatQuery: prompt });
  }

  const screens = {
    dashboards:<WorkspaceDashboards account={account}/>,
    evidence:<Investigations view="evidence" account={account}/>,
    "log-analysis":<Investigations view="log-analysis" account={account}/>,
    rca:<Investigations view="rca" account={account}/>,
    "release-notes":<ReleaseNotes/>,
    dashboard:<Dashboard onNavigate={nav}/>,
    chat:<Chat onOpenLogs={openLogsDomain} onNavigate={nav} initialQuery={chatInitialQuery} onInitialQueryConsumed={() => setChatInitialQuery(null)} initialKbId={chatInitialKb} accountInitials={identity.initials} toast={toast} />,
    logs:<LogsPage domain={logsDomain} onDomainChange={setLogsDomain} onOpenIncident={openIncident} initialQuery={logsInitialQuery} onInitialQueryConsumed={() => setLogsInitialQuery(null)}/>,
    incidents:<ItsmIncidents initialIncidentId={itsmIncidentId} onOpenLogs={openIncidentLogs} />,
    changes:<ItsmChanges initialChangeId={itsmChangeId} onOpenIncident={openIncident} onOpenUpgradeRunbook={openUpgradeRunbook} />,
    cmdb:<ItsmCmdb onOpenIncident={openIncident} />,
    kb:<KnowledgeBases toast={toast} onNavigate={nav}/>,
    ...(SHOW_LEARN_UI
      ? {
          "learn-topics": <TopicsLearn onAskChat={openK8sChat} onNavigate={nav} />,
          "learn-k8s": <KubernetesLearn onAskChat={openK8sChat} />,
        }
      : {}),
    docs:<Documents toast={toast} onNavigate={nav}/>, agents:<Agents toast={toast}/>, analytics:<Analytics/>,
    users:<Users toast={toast}/>, billing:<Billing toast={toast}/>, apikeys:<APIKeys toast={toast}/>,
    integrations:<Integrations toast={toast} onNavigate={nav} onAsk={(prompt) => nav("chat", { chatQuery: prompt })}/>, audit:<AuditLogs/>,
    notifs:<Notifications toast={toast}/>, settings:<Settings toast={toast} onSignOut={signOut} account={account}/>
  };

  const sections = {
    main:   ["dashboard","dashboards","chat","agents","analytics"],
    investigate: ["evidence", "log-analysis", "rca", "logs"],
    knowledge: ["kb","docs","integrations"],
    ...(SHOW_LEARN_UI ? { learn: ["learn-topics", "learn-k8s"] } : {}),
    itsm:   ["incidents","changes","cmdb"],
    config: ["users","billing","apikeys","audit","notifs","settings","release-notes"],
  };
  const sectionLabels = { main:"Operate", investigate:"Investigate", knowledge:"Knowledge", learn:"Learn", itsm:"Service management", config:"Govern" };

  return (
    <div className="shell production-console" data-theme={theme}>
      <a className="console-skip-link" href="#main-content" inert={mobileNav && sideOpen} onClick={event => {event.preventDefault();mainContentRef.current?.focus();}}>Skip to main content</a>
      {/* Overlay */}
      {sideOpen && <div className="overlay" onClick={()=>setSideOpen(false)}/>}

      {/* Sidebar */}
      <aside ref={sidebarRef} id="workspace-navigation" className={`sidebar ${sideOpen?"open":""}`} inert={mobileNav && !sideOpen} role={mobileNav && sideOpen ? "dialog" : undefined} aria-modal={mobileNav && sideOpen ? true : undefined} aria-label="Workspace navigation">
        <div className="sidebar-logo">
          <div className="logo-mark"><span>W</span></div>
          <span className="logo-text">We<em>crew</em> Copilot</span>
          <button className="sidebar-close" aria-label="Close navigation" onClick={()=>setSideOpen(false)}>✕</button>
        </div>
        <nav className="sidebar-nav">
          {Object.entries(sections).map(([sec,ids])=>(
            <div key={sec}>
              <div className="nav-section">{sectionLabels[sec]}</div>
              {ids.map(id=>{
                const n = NAV_ITEMS.find(x=>x.id===id);
                return (
                  <button type="button" key={id} className={`nav-item ${page===id?"active":""}`} aria-current={page===id?"page":undefined} onClick={()=>nav(id)}>
                    <span className="nav-icon"><ConsoleIcon name={id}/></span>
                    <span>{n?.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <button type="button" className="sidebar-footer" onClick={() => { if (getToken()) signOut(); else {setSideOpen(false);goRoute("signin");} }} aria-label={getToken() ? "Sign out" : "Sign in"}>
          <span className="avatar">{identity.initials}</span>
          <span className="flex-1 min-w-0">
            <span className="user-name">{identity.displayName}</span>
            <span className="user-role">{identity.role}</span>
          </span>
          <span className="sidebar-signout-btn">{getToken() ? "Sign out" : "Sign in"}</span>
        </button>
      </aside>

      {/* Main */}
      <div className="main" inert={mobileNav && sideOpen}>
        <header className="topbar">
          <button ref={menuRef} className="hamburger" aria-label="Open navigation" aria-expanded={sideOpen} aria-controls="workspace-navigation" onClick={()=>setSideOpen(true)}><ConsoleIcon name="menu"/></button>
          <div className="topbar-title"><span className="workspace-crumb">Workspace / </span>{NAV_ITEMS.find(n=>n.id===page)?.label}</div>
          <span className="console-mode">{identity.signedIn ? identity.displayName : "Local preview"}</span>
          <button type="button" className="theme-toggle" onClick={toggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              {theme === "dark" ? <><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5"/></> : <path d="M20 15.5A9 9 0 0 1 8.5 4 9 9 0 1 0 20 15.5Z"/>}
            </svg><span>{theme === "dark" ? "Light" : "Dark"}</span>
          </button>
          <div className="user-menu-wrap">
            <div
              className="avatar"
              style={{cursor:"pointer",flexShrink:0}}
              onClick={() => setUserMenuOpen((o) => !o)}
              title="Account menu"
              role="button"
              tabIndex={0}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              onKeyDown={(e) => e.key === "Enter" && setUserMenuOpen((o) => !o)}
            >
              {identity.initials}
            </div>
            {userMenuOpen && (
              <>
                <div className="overlay" style={{zIndex:250,background:"transparent"}} onClick={() => setUserMenuOpen(false)} />
                <div className="user-menu" role="menu">
                  <div className="user-menu-head">
                    <div className="user-menu-name">{identity.displayName}</div>
                    <div className="user-menu-sub">{identity.email || identity.role}</div>
                  </div>
                  <button type="button" className="user-menu-item" onClick={() => { setUserMenuOpen(false); nav("settings"); }}>
                    ⚙ Settings
                  </button>
                  <button type="button" className="user-menu-item danger" onClick={signOut}>
                    ⎋ Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main ref={mainContentRef} id="main-content" className="content" tabIndex={-1}>
          {["logs", "incidents", "changes", "cmdb"].includes(page) && <aside className="int-notice operational-demo" role="note" aria-label="Demonstration data"><strong>Demonstration data</strong><span>These records, statuses, metrics, and approval windows are local examples—not live operations. Filters and linked views explore the examples; they do not change infrastructure or submit incidents.</span></aside>}
          <Suspense fallback={<RouteFallback />}>
            {screens[page] || screens.dashboard}
          </Suspense>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="bottom-nav">
        <div className="bottom-nav-inner">
          {BOTTOM_NAV.map(n=>(
            <div key={n.id} className={`bn-item ${page===n.id?"active":""}`} onClick={()=>nav(n.id)}>
              <span className="bn-icon">{n.icon}</span>
              <span className="bn-label">{n.label}</span>
            </div>
          ))}
        </div>
      </nav>

      {/* Toasts */}
      <div className="toast-wrap" role="status" aria-live="polite" aria-relevant="additions">
        {toasts.map(t=>(
          <div key={t.id} className="toast">
            <span aria-hidden="true">{t.type==="success"?"✅":t.type==="info"?"ℹ️":"⚠️"}</span>
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
